import type { Language } from '@/contexts/LanguageContext';

export interface BlogPostTranslation {
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
}

export interface BlogPost {
  id: string;
  slug: string;
  image_url: string;
  published_at: string;
  translations: {
    pt: BlogPostTranslation;
    en: BlogPostTranslation;
  };
}

export type LocalizedBlogPost = Omit<BlogPost, 'translations'> & BlogPostTranslation;

export const blogPosts: BlogPost[] = [
  {
    id: '22',
    slug: 'biomassa-vs-coque-verde-petroleo-reducao-gases-efeito-estufa',
    image_url: '/media/biomassa-coque-verde-petroleo-gee.svg',
    published_at: '2026-07-23',
    translations: {
      pt: {
        title: 'Biomassa vs. coque verde de petróleo: qual reduz mais gases de efeito estufa na indústria?',
        excerpt:
          'Indústrias de cal, cimento e cerâmica escolhem entre biomassa e coque verde de petróleo para alimentar seus fornos. Entenda o impacto de cada combustível nas emissões de GEE e nos custos operacionais.',
        content: `
      <h2>Combustível de forno: uma decisão que também é ambiental</h2>
      <p>Nos fornos de cal, cimento, cerâmica e outros processos de calcinação, o combustível responde por boa parte do custo de produção — mas também por boa parte da pegada de carbono da indústria. Duas opções concorrem diretamente pelo mesmo espaço na matriz energética desses fornos: o <strong>coque verde de petróleo</strong> (green petroleum coke, ou GPC) e a <strong>biomassa</strong> (cavaco de eucalipto, bagaço de cana, casca de arroz e outros resíduos agroflorestais). A escolha entre eles não é apenas técnica e econômica — é também uma decisão climática, com efeitos diretos no inventário de gases de efeito estufa (GEE) da empresa.</p>

      <h2>O que é o coque verde de petróleo?</h2>
      <p>O coque verde de petróleo é um subproduto sólido do refino de petróleo, obtido no processo de coqueamento retardado. É um combustível de altíssima densidade energética — o poder calorífico típico fica entre 8.000 e 8.500 kcal/kg, próximo ou superior ao de carvões metalúrgicos — com baixo teor de cinzas e alto teor de carbono fixo (frequentemente acima de 85–90%). Essas características o tornam atrativo para fornos que exigem alta temperatura de chama e grande volume de energia por tonelada processada.</p>
      <p>O problema ambiental está em dois pontos: primeiro, é um combustível <strong>100% fóssil</strong> — todo o carbono liberado na combustão é carbono que estava geologicamente sequestrado há milhões de anos, e sua queima representa adição líquida de CO₂ à atmosfera. Segundo, o coque de petróleo costuma ter teor de enxofre elevado (podendo variar de cerca de 1% a mais de 6%, dependendo do petróleo de origem), o que aumenta as emissões de SOx e a necessidade de sistemas de lavagem de gases — muitas vezes usando cal como reagente, em um contraste irônico entre o combustível e o próprio produto do forno.</p>

      <h2>O que é a biomassa como combustível industrial?</h2>
      <p>Biomassa, no contexto de fornos industriais, refere-se a resíduos e subprodutos de origem vegetal: cavaco e serragem de madeira (muitas vezes de eucalipto de reflorestamento certificado), bagaço de cana-de-açúcar, casca de arroz, casca de café e similares. O poder calorífico é bem mais baixo que o do coque — normalmente entre 2.800 e 4.500 kcal/kg, dependendo da umidade e do tipo de resíduo —, o que significa que é preciso queimar um volume físico maior para entregar a mesma energia térmica.</p>
      <p>A vantagem ambiental está na origem do carbono: o CO₂ liberado na queima da biomassa é <strong>carbono biogênico</strong>, ou seja, foi capturado da atmosfera há poucos anos ou décadas, durante o crescimento da planta. Metodologias de inventário como o GHG Protocol e as diretrizes do IPCC tratam esse CO₂ biogênico separadamente do CO₂ fóssil — ele é reportado, mas normalmente não soma ao total de emissões de Escopo 1 da empresa, desde que a biomassa venha de fontes manejadas de forma sustentável (sem desmatamento líquido ou conversão de uso do solo).</p>

      <h2>Comparando o potencial de redução de GEE</h2>
      <p>Em termos práticos, cada gigajoule (GJ) de energia térmica gerado a partir de coque de petróleo adiciona carbono fóssil ao ciclo atmosférico, enquanto o mesmo GJ gerado a partir de biomassa certificada é contabilizado como neutro no inventário de emissões fósseis. Isso significa que a <strong>taxa de substituição térmica</strong> — a fração da energia do forno que passa a vir de biomassa em vez de coque — se traduz de forma praticamente direta em redução de emissões fósseis reportadas:</p>
      <ul>
        <li>Substituir 20% da energia térmica por biomassa reduz, em ordem de grandeza, cerca de 20% do CO₂ fóssil de Escopo 1 associado ao forno.</li>
        <li>Substituir 40% — patamar já demonstrado em fornos adaptados para co-firing (queima simultânea de dois combustíveis) sem grandes intervenções estruturais — aproxima-se de uma redução de 40% no CO₂ fóssil do processo de combustão.</li>
        <li>A substituição total (100%) eliminaria o CO₂ fóssil da queima, mas raramente é viável tecnicamente sem redesenho do sistema de alimentação e do próprio forno, dada a diferença de densidade energética entre os dois combustíveis.</li>
      </ul>
      <p>Além do CO₂, a troca reduz proporcionalmente o enxofre introduzido no processo — já que a biomassa tem teor de enxofre próximo de zero —, aliviando a carga sobre sistemas de dessulfurização e reduzindo o consumo de reagentes alcalinos usados para neutralizar SOx.</p>

      <h2>O que essa conta não pode ignorar</h2>
      <p>É importante não tratar a neutralidade da biomassa como automática. A conta de ciclo de vida completo (do inglês <em>well-to-wheel</em> ou <em>cradle-to-gate</em>) inclui as emissões de cultivo, colheita, processamento e — principalmente — transporte da biomassa até o forno. Como o poder calorífico é menor, é preciso transportar de 2 a 3 vezes mais massa de biomassa do que de coque para entregar a mesma energia, o que pode gerar emissões logísticas relevantes se a fonte estiver distante da planta industrial. Por isso, a viabilidade ambiental real da substituição depende da proximidade de fontes de biomassa residual (evitando cultivo dedicado) e de rotas logísticas eficientes.</p>
      <p>Também vale destacar que apenas biomassa de origem manejada e rastreável garante neutralidade de carbono no inventário — resíduos de desmatamento ilegal ou de conversão de vegetação nativa não devem ser contabilizados como neutros, e certificações (como FSC ou Cerflor, para biomassa florestal) são a forma de comprovar essa origem perante auditorias e mercados de carbono.</p>

      <h2>Trade-offs operacionais entre os dois combustíveis</h2>
      <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:0.95em;">
        <thead>
          <tr style="background:#f3f4f6;">
            <th style="text-align:left;padding:8px 12px;border:1px solid #e5e7eb;">Critério</th>
            <th style="text-align:left;padding:8px 12px;border:1px solid #e5e7eb;">Coque verde de petróleo</th>
            <th style="text-align:left;padding:8px 12px;border:1px solid #e5e7eb;">Biomassa</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Poder calorífico</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Alto (≈ 8.000–8.500 kcal/kg)</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Moderado a baixo (≈ 2.800–4.500 kcal/kg)</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Origem do carbono</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Fóssil (adiciona CO₂ líquido)</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Biogênica (neutra, se certificada)</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Enxofre</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Variável, pode ser elevado</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Próximo de zero</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Logística</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Volume menor, mais simples</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Volume 2–3x maior para mesma energia</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Adaptação do forno</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Geralmente já compatível</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Pode exigir sistema de alimentação dedicado</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Disponibilidade</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Ligada à oferta de refino</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Sazonal, depende de fonte regional</td>
          </tr>
        </tbody>
      </table>

      <h2>O contexto regulatório e de mercado de carbono</h2>
      <p>Com a estruturação do mercado regulado de carbono no Brasil (Lei nº 15.042/2024, que cria o Sistema Brasileiro de Comércio de Emissões — SBCE) e a pressão crescente de clientes industriais por relatórios ESG e inventários de GEE auditáveis, a taxa de substituição de combustíveis fósseis por biomassa deixa de ser apenas uma escolha de custo e passa a ser também um ativo estratégico: reduz exposição a precificação futura de carbono e fortalece a posição da empresa em cadeias de fornecimento que já exigem rastreabilidade de emissões.</p>

      <h2>Conclusão</h2>
      <p>Não existe um combustível "vencedor" absoluto — a decisão depende da disponibilidade regional de biomassa residual, da distância logística até o forno, dos limites de enxofre exigidos pela licença ambiental e da estratégia de carbono da empresa. Mas o padrão é claro: cada ponto percentual de substituição de coque verde de petróleo por biomassa certificada reduz, de forma aproximadamente proporcional, o carbono fóssil emitido pelo forno — e ainda alivia a carga de enxofre sobre os sistemas de controle de emissões. Para operações que já dependem de cal em seus processos, entender essa equação energética é parte do mesmo esforço de eficiência e responsabilidade ambiental que orienta a escolha de qualquer insumo crítico.</p>
    `,
        category: 'Meio Ambiente',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Biomass vs. green petroleum coke: which cuts more greenhouse gases in industry?',
        excerpt:
          'Lime, cement, and ceramics kilns choose between biomass and green petroleum coke as fuel. Understand the GHG impact and operational trade-offs of each option.',
        content: `
      <h2>Kiln fuel: a decision that is also environmental</h2>
      <p>In lime, cement, ceramics, and other calcination kilns, fuel accounts for a large share of production cost — and of the industry's carbon footprint. Two options compete directly for the same share of the energy mix: <strong>green petroleum coke</strong> (GPC) and <strong>biomass</strong> (eucalyptus wood chips, sugarcane bagasse, rice husk, and other agroforestry residues). The choice between them is not only technical and economic — it is also a climate decision with direct effects on the company's greenhouse gas (GHG) inventory.</p>

      <h2>What is green petroleum coke?</h2>
      <p>Green petroleum coke is a solid byproduct of oil refining, obtained through the delayed coking process. It is an extremely energy-dense fuel — typical calorific value ranges from 8,000 to 8,500 kcal/kg, close to or above metallurgical coal — with low ash content and high fixed carbon (often above 85–90%). These traits make it attractive for kilns that require high flame temperature and large energy volumes per ton processed.</p>
      <p>The environmental problem lies in two points: first, it is a <strong>100% fossil</strong> fuel — all carbon released in combustion was geologically sequestered millions of years ago, so burning it represents a net addition of CO₂ to the atmosphere. Second, petroleum coke often carries a high sulfur content (ranging roughly from 1% to over 6%, depending on the source crude), which increases SOx emissions and the need for flue-gas scrubbing systems — frequently using lime as the reagent, an ironic contrast between the fuel and the kiln's own product.</p>

      <h2>What is biomass as an industrial fuel?</h2>
      <p>In the context of industrial kilns, biomass refers to plant-based residues and byproducts: wood chips and sawdust (often from certified reforested eucalyptus), sugarcane bagasse, rice husk, coffee husk, and similar materials. Calorific value is considerably lower than coke — typically between 2,800 and 4,500 kcal/kg depending on moisture and residue type — meaning a larger physical volume must be burned to deliver the same thermal energy.</p>
      <p>The environmental advantage lies in the origin of the carbon: CO₂ released when burning biomass is <strong>biogenic carbon</strong> — it was captured from the atmosphere only years or decades earlier, during plant growth. Inventory methodologies such as the GHG Protocol and IPCC guidelines treat this biogenic CO₂ separately from fossil CO₂ — it is reported, but typically does not add to the company's Scope 1 emissions total, provided the biomass comes from sustainably managed sources (no net deforestation or land-use conversion).</p>

      <h2>Comparing the GHG reduction potential</h2>
      <p>In practical terms, every gigajoule (GJ) of thermal energy generated from petroleum coke adds fossil carbon to the atmospheric cycle, while the same GJ generated from certified biomass is accounted as neutral in the fossil emissions inventory. This means the <strong>thermal substitution rate</strong> — the fraction of kiln energy switched from coke to biomass — translates almost directly into a reduction of reported fossil emissions:</p>
      <ul>
        <li>Replacing 20% of thermal energy with biomass cuts, roughly, about 20% of the Scope 1 fossil CO₂ associated with the kiln.</li>
        <li>Replacing 40% — a level already demonstrated in kilns adapted for co-firing without major structural changes — approaches a 40% cut in fossil CO₂ from the combustion process.</li>
        <li>Full (100%) substitution would eliminate fossil CO₂ from combustion, but is rarely feasible without redesigning the feeding system and the kiln itself, given the energy-density gap between the two fuels.</li>
      </ul>
      <p>Beyond CO₂, the switch proportionally reduces the sulfur introduced into the process — since biomass sulfur content is near zero — easing the load on desulfurization systems and reducing consumption of alkaline reagents used to neutralize SOx.</p>

      <h2>What this math cannot ignore</h2>
      <p>Biomass neutrality should not be treated as automatic. A full lifecycle accounting (well-to-wheel or cradle-to-gate) includes emissions from cultivation, harvesting, processing, and — critically — transporting the biomass to the kiln. Because calorific value is lower, 2 to 3 times more biomass mass must be transported than coke to deliver the same energy, which can generate significant logistics emissions if the source is far from the industrial plant. Real environmental viability therefore depends on proximity to residual biomass sources (avoiding dedicated cultivation) and efficient logistics routes.</p>
      <p>It is also worth noting that only biomass from managed, traceable sources guarantees carbon neutrality in the inventory — residues from illegal deforestation or native vegetation conversion must not be counted as neutral, and certifications (such as FSC, for forestry biomass) are how this origin is proven to auditors and carbon markets.</p>

      <h2>Operational trade-offs between the two fuels</h2>
      <table style="width:100%;border-collapse:collapse;margin:16px 0;font-size:0.95em;">
        <thead>
          <tr style="background:#f3f4f6;">
            <th style="text-align:left;padding:8px 12px;border:1px solid #e5e7eb;">Criterion</th>
            <th style="text-align:left;padding:8px 12px;border:1px solid #e5e7eb;">Green petroleum coke</th>
            <th style="text-align:left;padding:8px 12px;border:1px solid #e5e7eb;">Biomass</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Calorific value</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">High (≈ 8,000–8,500 kcal/kg)</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Moderate to low (≈ 2,800–4,500 kcal/kg)</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Carbon origin</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Fossil (net CO₂ addition)</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Biogenic (neutral, if certified)</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Sulfur</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Variable, can be high</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Near zero</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Logistics</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Smaller volume, simpler</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">2–3x more volume for same energy</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Kiln adaptation</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Generally already compatible</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">May require dedicated feeding system</td>
          </tr>
          <tr>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Availability</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Tied to refining supply</td>
            <td style="padding:8px 12px;border:1px solid #e5e7eb;">Seasonal, depends on regional source</td>
          </tr>
        </tbody>
      </table>

      <h2>Regulatory and carbon market context</h2>
      <p>With Brazil structuring its regulated carbon market (Law 15,042/2024, which creates the Brazilian Emissions Trading System — SBCE) and growing pressure from industrial customers for auditable ESG reports and GHG inventories, the rate of fossil-to-biomass fuel substitution is no longer just a cost choice — it is also a strategic asset, reducing exposure to future carbon pricing and strengthening the company's position in supply chains that already demand emissions traceability.</p>

      <h2>Conclusion</h2>
      <p>There is no absolute "winning" fuel — the decision depends on regional availability of residual biomass, logistics distance to the kiln, sulfur limits set by environmental permits, and the company's carbon strategy. But the pattern is clear: every percentage point of green petroleum coke replaced by certified biomass cuts, roughly proportionally, the fossil carbon emitted by the kiln — while also easing the sulfur load on emissions-control systems. For operations that already depend on lime in their processes, understanding this energy equation is part of the same efficiency and environmental responsibility effort that guides the choice of any critical input.</p>
    `,
        category: 'Environment',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '21',
    slug: 'cal-agricola-calcario-dolomitico-viabilidade-mgo',
    image_url: '/media/cal-agricultura-solo.jpg',
    published_at: '2026-03-18',
    translations: {
      pt: {
        title: 'Cal virgem dolomítica vs calcário: qual é mais viável para corrigir MgO?',
        excerpt:
          'A cal virgem custa 5× mais por tonelada — mas pode ser 48% mais barata por kg de MgO entregue. Entenda a matemática por trás dessa escolha e use a calculadora para simular sua situação.',
        content: `
      <h2>O que você vai descobrir neste artigo</h2>
      <p>A cal virgem dolomítica custa, em média, 5,4 vezes mais por tonelada do que o calcário dolomítico comum. Mas quando analisamos o custo por quilograma de MgO efetivamente entregue na lavoura, o resultado é surpreendente — e muda a decisão de compra na maioria dos casos.</p>

      <h2>O problema da acidez e da deficiência de magnésio nos solos brasileiros</h2>
      <p>A acidez do solo é um dos maiores entraves à produtividade agrícola no Brasil. Nossos solos tropicais são naturalmente ácidos, resultado de milênios de intemperismo intenso e lixiviação, e a correção com produtos calcários é a principal estratégia para elevar o pH, fornecer cálcio e magnésio, e criar condições adequadas para o desenvolvimento das culturas.</p>
      <p>O magnésio (Mg) é o único macronutriente mineral que compõe a molécula de clorofila. Sem Mg suficiente, a planta não consegue realizar a fotossíntese de forma plena. Além disso, o Mg participa da ativação de dezenas de enzimas, da absorção de fósforo e da transferência de energia celular. Reduções de até 40% no rendimento de soja já foram documentadas em condições de deficiência severa de magnésio (Fageria, 2001).</p>
      <p>Quando há deficiência de magnésio no solo, surge a dúvida: é mais vantajoso usar o tradicional calcário dolomítico ou a cal virgem dolomítica?</p>

      <h2>O que são esses produtos?</h2>
      <p>O <strong>calcário dolomítico</strong> é uma rocha sedimentar formada por dolomita (CaMg(CO₃)₂). Para ser classificado como dolomítico pela legislação brasileira, deve conter MgO superior a 12% — embora no mercado encontremos materiais com 6% a 14%, dependendo da jazida. Trata-se de um produto de reação lenta: os carbonatos levam de 3 a 6 meses para atingir o efeito pleno no solo.</p>
      <p>A <strong>cal virgem dolomítica</strong> é obtida pela calcinação do calcário dolomítico a temperaturas entre 900°C e 1.000°C. Nesse processo, o CO₂ é eliminado e os carbonatos se transformam em óxidos muito mais concentrados e reativos: CaO e MgO. A neutralização da acidez ocorre em dias a semanas — não meses. E o teor de MgO típico é de 35%, quase 4,4 vezes mais do que um calcário com 8% de MgO.</p>

      <h2>PRNT: o indicador que tudo resume</h2>
      <p>O <strong>Poder Relativo de Neutralização Total (PRNT)</strong> é o principal índice de qualidade dos corretivos agrícolas no Brasil, definido pela Instrução Normativa MAPA nº 35/2006. Ele é obrigatório no laudo de qualidade de todo corretivo comercializado e é a base do cálculo de dose recomendado por todas as principais tabelas de adubação do país (CQFS, IAC, Embrapa Cerrados).</p>
      <p>O PRNT combina dois fatores:</p>
      <ul>
        <li><strong>PN — Poder de Neutralização:</strong> capacidade química de neutralizar acidez, expressa como porcentagem equivalente ao CaCO₃ puro (= 100%). Óxidos como CaO (PN = 179%) e MgO (PN = 248%) são muito mais eficientes por kg do que os carbonatos, pois após hidrólise liberam hidróxidos de alta reatividade.</li>
        <li><strong>RE — Reatividade (granulometria):</strong> proporção do produto que reage no solo em até 90 dias, determinada pela distribuição de peneiras. Material mais fino reage mais rápido — por isso a cal virgem, com 90% passando na peneira 200 mesh (0,075 mm), tem RE ≈ 90%, enquanto calcários grosseiros podem ter RE de 40–60%.</li>
      </ul>
      <p style="font-family:monospace;background:#f0fdf4;padding:10px 14px;border-radius:8px;font-size:0.93em;border:1px solid #86efac;"><strong>PRNT (%) = PN (%) × RE (%) ÷ 100</strong></p>
      <p>Aplicando a fórmula para os produtos deste artigo:</p>
      <ul>
        <li><strong>Calcário dolomítico (8% MgO, típico):</strong> PN ≈ 100% × RE ≈ 75% → <strong>PRNT ≈ 75%</strong></li>
        <li><strong>Cal virgem dolomítica (35% MgO, 10% ret. 200 mesh):</strong> PN ≈ 185% × RE ≈ 90% → <strong>PRNT ≈ 167%</strong></li>
      </ul>
      <p>Na prática, isso significa que a cal virgem tem um poder de correção <strong>2,2 vezes maior</strong> por tonelada do que o calcário. Para atingir o mesmo efeito de calagem (mesmo aumento de pH), é preciso aplicar muito menos cal. Esse é o segundo fator — além do MgO concentrado — que determina a vantagem logística e econômica da cal virgem.</p>
      <p>O PRNT também é central no cálculo de dose. A fórmula padrão é: <em>Dose corretivo = Dose CaCO₃ puro ÷ (PRNT ÷ 100)</em>. Um calcário com PRNT 75% exige 33% mais tonelagem do que um produto com PRNT 100%, e a cal com PRNT 167% exige 40% menos do que o produto padrão. Esses valores impactam diretamente no custo de transporte e aplicação.</p>

      <h2>A análise econômica: o resultado surpreendente</h2>
      <p>Considerando os preços padrão praticados — calcário a R$120/ton + R$300 de frete = R$420/ton entregue; cal virgem a R$650/ton + R$300 de frete = R$950/ton entregue — os números revelam uma inversão contraintuitiva:</p>
      <ul>
        <li>Calcário: 80 kg de MgO por tonelada → custo de <strong>R$ 5,25 por kg de MgO</strong></li>
        <li>Cal virgem: 350 kg de MgO por tonelada → custo de <strong>R$ 2,71 por kg de MgO</strong></li>
      </ul>
      <p>A cal virgem é <strong>48% mais barata por kg de MgO entregue na fazenda</strong>, apesar de custar 2,26 vezes mais por tonelada. O frete — idêntico por tonelada — pesa proporcionalmente muito mais no calcário, pois é preciso transportar quase 4,4 vezes mais tonelagens para entregar a mesma correção de magnésio.</p>
      <p>Para corrigir 1 tonelada de MgO no solo, o produtor precisa de 12,5 toneladas de calcário (custo R$5.250) versus apenas 2,86 toneladas de cal virgem (custo R$2.714) — uma economia de R$2.535 ou 48%.</p>
      <p>O ponto de equilíbrio (breakeven) só é atingido se a cal virgem chegar a custar R$1.837/ton entregue. No preço atual de R$950/ton, há uma margem de R$887/ton antes de perder viabilidade econômica.</p>

      <h2>Calagem versus correção de MgO: duas finalidades distintas</h2>
      <p>É fundamental distinguir dois objetivos que podem coexistir ou não. Quando o objetivo é corrigir o pH (calagem), o parâmetro relevante é o PRNT. Quando o objetivo é suprir a deficiência de magnésio, o parâmetro relevante é o teor de MgO × dose aplicada. Em muitas situações, ambos os objetivos estão presentes — e a calculadora abaixo permite analisar cada um separadamente ou em conjunto.</p>

      <h2>Riscos e limitações da cal virgem</h2>
      <p>O PRNT elevado da cal virgem é uma faca de dois gumes. Se a dose for mal calculada ou aplicada de forma desuniforme, o pH pode ultrapassar 7,0–7,5, causando supercalagem. Nessa situação, micronutrientes como Zn, Mn, B e Fe ficam insolúveis (Lindsay & Norvell, 1978), o fósforo pode precipitar com cálcio, e populações de bactérias fixadoras de N₂ podem ser prejudicadas. Além disso, com 90% do material passando na peneira 200 mesh, a cal é extremamente fina durante a aplicação, exigindo EPIs adequados. O efeito residual também é menor (6–18 meses versus 2–4 anos do calcário), demandando maior frequência de reaplicação.</p>

      <h2>Quando usar cada produto?</h2>
      <p>A cal virgem dolomítica é recomendada quando há deficiência severa de MgO e o calcário disponível tem baixo teor de magnésio, quando é necessário corrigir o pH com urgência antes do plantio, quando a distância da jazida torna o frete do calcário proibitivo para os grandes volumes necessários, e sempre com controle rigoroso de dose e assistência técnica qualificada. O calcário dolomítico é preferível para correção de rotina e planejada sem urgência, quando o solo já está com pH próximo de 5,5–6,0 (risco maior de supercalagem com cal), quando não há assistência técnica disponível para calcular a dose correta de cal, ou quando a lavoura já tem cultura implantada.</p>

      <h2>Calculadora de viabilidade: Cal vs Calcário</h2>
      <p>Use a calculadora abaixo para comparar os dois produtos na sua situação específica. Ela incorpora o PRNT em todas as análises e possui três modos:</p>
      <ul>
        <li><strong>Por MgO + PRNT:</strong> informe a deficiência de MgO (kg/ha) e o PRNT de cada produto — a calculadora compara o custo de correção do MgO e verifica se a dose calculada é compatível com a dose de calagem, alertando automaticamente sobre risco de supercalagem ou subdose.</li>
        <li><strong>Por Calagem (PRNT):</strong> informe a dose de calcário recomendada pelo agrônomo e os PRNT de cada produto — a calculadora converte para dose equivalente de cal virgem e compara os custos para o mesmo efeito de pH.</li>
        <li><strong>Análise de Preço:</strong> mostra o custo por kg de MgO e por unidade de PRNT de cada produto, com dois pontos de breakeven (um para MgO, outro para calagem).</li>
      </ul>

      <div style="border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fafafa;max-width:900px;">
        <iframe
          src="/calculadora-cal-calcario.html"
          title="Calculadora Cal Virgem vs Calcário Dolomítico — MgO e PRNT"
          loading="lazy"
          style="width:100%;height:1700px;border:0;display:block;"
        ></iframe>
      </div>

      <p style="margin-top:10px;font-size:13px;color:#6b7280;"><strong>Privacidade:</strong> os dados inseridos na calculadora são processados apenas no seu navegador e <strong>não são armazenados</strong>.</p>

      <h2>Referências</h2>
      <ul>
        <li>MAPA. Instrução Normativa nº 35, de 4 de julho de 2006. Normas sobre especificações e garantias para corretivos agrícolas.</li>
        <li>CQFS RS/SC. Manual de adubação e de calagem para os estados do RS e SC. 11. ed. Porto Alegre: SBCS, 2016.</li>
        <li>RAIJ, B. van et al. Recomendações de adubação e calagem para o Estado de São Paulo. 2. ed. Campinas: IAC, 2001.</li>
        <li>SOUSA, D.M.G.; LOBATO, E. Cerrado: correção do solo e adubação. 2. ed. Planaltina: Embrapa Cerrados, 2004.</li>
        <li>FAGERIA, N.K. Nutrient interactions in crop plants. Journal of Plant Nutrition, v.24, n.8, p.1269-1290, 2001.</li>
        <li>LINDSAY, W.L.; NORVELL, W.A. Development of a DTPA soil test for zinc, iron, manganese, and copper. SSSAJ, v.42, p.421-428, 1978.</li>
      </ul>
    `,
        category: 'Agricultura',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Dolomitic quicklime vs limestone: which is more cost-effective for MgO correction?',
        excerpt:
          'Quicklime costs 5× more per ton — but can be 48% cheaper per kg of MgO delivered. Understand the math and use the calculator to simulate your scenario.',
        content: `
      <h2>The surprising economics of MgO correction</h2>
      <p>Dolomitic quicklime costs, on average, 5.4 times more per ton than common dolomitic limestone. But when we analyze the cost per kilogram of MgO actually delivered to the field, the result is counterintuitive — and changes the purchasing decision in most cases.</p>
      <p>Magnesium (Mg) is the only mineral macronutrient that makes up the chlorophyll molecule. Without sufficient Mg, the plant cannot carry out photosynthesis efficiently. Studies have documented yield reductions of up to 40% in soybean under severe magnesium deficiency (Fageria, 2001).</p>

      <h2>Understanding the products</h2>
      <p><strong>Dolomitic limestone</strong> is a sedimentary rock composed of dolomite (CaMg(CO₃)₂), with a typical MgO content of 6–14% depending on the quarry. It reacts slowly in the soil — 3 to 6 months to reach full effect. <strong>Dolomitic quicklime</strong> is produced by calcining limestone at 900–1,000°C, converting carbonates into highly reactive oxides (CaO and MgO). It neutralizes acidity in days to weeks and typically contains 35% MgO — 4.4× more magnesium per ton than an 8% MgO limestone.</p>

      <h2>The economic analysis</h2>
      <p>Using standard market prices — limestone at R$420/ton delivered; quicklime at R$950/ton delivered — the numbers reveal a striking inversion:</p>
      <ul>
        <li>Limestone: 80 kg of MgO per ton → <strong>R$ 5.25 per kg of MgO</strong></li>
        <li>Quicklime: 350 kg of MgO per ton → <strong>R$ 2.71 per kg of MgO</strong></li>
      </ul>
      <p>Quicklime is <strong>48% cheaper per kg of MgO delivered</strong>, despite costing 2.26× more per ton. To correct 1 ton of soil MgO: 12.5 tons of limestone (R$5,250) versus 2.86 tons of quicklime (R$2,714) — a saving of R$2,535. The breakeven price for quicklime is R$1,837/ton delivered; at the current R$950/ton, there is a R$887/ton margin.</p>

      <h2>Risks of quicklime</h2>
      <p>The high PRNT (~167%) of dolomitic quicklime requires precise dosing. Over-liming (pH above 7.0–7.5) leads to micronutrient lock-up (Zn, Mn, B, Fe), phosphate precipitation, and potential impact on nitrogen-fixing bacteria. Its ultra-fine particle size requires proper PPE during application, and its residual effect is shorter (6–18 months vs 2–4 years for limestone).</p>

      <h2>MgO correction calculator</h2>
      <p>Use the interactive calculator below to compare costs for your specific situation — by MgO deficiency, by liming equivalence (PRNT), or by price breakeven analysis.</p>

      <div style="border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fafafa;max-width:900px;">
        <iframe
          src="/calculadora-cal-calcario.html"
          title="Quicklime vs Limestone Cost Calculator"
          loading="lazy"
          style="width:100%;height:1300px;border:0;display:block;"
        ></iframe>
      </div>

      <p style="margin-top:10px;font-size:13px;color:#6b7280;"><strong>Privacy:</strong> data entered in the calculator is processed locally in your browser and <strong>is not stored</strong>.</p>
    `,
        category: 'Agriculture',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '20',
    slug: 'controle-custos-industria-cal',
    image_url: '/media/forno_de_cal_custo_calculadora_quimical.png',
    published_at: '2026-02-10',
    translations: {
      pt: {
        title: 'Controle de custos na indústria de cal: por que isso define sua margem',
        excerpt:
          'Entenda como o controle de custos em combustível, energia, calcário, logística e produtividade protege a margem na indústria de cal — e simule ganhos com uma calculadora prática.',
        content: `
      <h2>Por que o controle de custos é decisivo para a indústria de cal?</h2>
      <p>Na indústria de cal, pequenas variações de custo por tonelada podem gerar impactos muito grandes no resultado mensal. Como o processo depende de <strong>energia térmica intensa</strong>, matéria-prima mineral e logística eficiente, a gestão de custos precisa ser contínua e orientada por indicadores.</p>
      <p>Quando a empresa mede corretamente cada componente do custo, ela ganha previsibilidade de margem, melhora a competitividade comercial e reduz o risco de perda de rentabilidade em cenários de volatilidade.</p>

      <h2>Os principais centros de custo que merecem monitoramento</h2>
      <ul>
        <li><strong>Combustível do forno:</strong> normalmente um dos itens mais relevantes no custo por tonelada.</li>
        <li><strong>Energia elétrica:</strong> afeta moagem, movimentação e etapas auxiliares do processo.</li>
        <li><strong>Calcário e rendimento operacional:</strong> qualidade da matéria-prima influencia consumo específico e perdas.</li>
        <li><strong>Mão de obra e manutenção:</strong> estabilidade operacional reduz paradas e retrabalho.</li>
        <li><strong>Frete e logística:</strong> distância até o cliente e eficiência de carregamento impactam fortemente a margem final.</li>
      </ul>

      <h2>Como transformar dados de custo em decisão estratégica</h2>
      <p>Um bom controle de custos não é apenas contábil: ele precisa apoiar decisões do dia a dia. Isso significa comparar cenários, como redução de consumo térmico, renegociação de frete, ajuste de produtividade e ganho de escala.</p>
      <p>Com simulações simples, gestores conseguem priorizar ações com maior retorno e justificar investimentos com base em números reais de impacto por tonelada e por mês.</p>

      <h2>Boas práticas para aumentar margem sem comprometer qualidade</h2>
      <ol>
        <li>Padronizar indicadores de custo por tonelada para toda a operação.</li>
        <li>Acompanhar variações semanais de combustível e energia.</li>
        <li>Relacionar eficiência de processo com custo final de produção.</li>
        <li>Trabalhar com metas de redução progressiva por centro de custo.</li>
        <li>Atualizar cenários de simulação antes de decisões comerciais relevantes.</li>
      </ol>

      <h2>Conclusão</h2>
      <p>Em um mercado pressionado por preço e eficiência, o controle de custos na indústria de cal deixa de ser diferencial e passa a ser requisito de sobrevivência. Empresas que medem, simulam e ajustam rápido conseguem proteger margem e crescer com maior segurança.</p>

      <h2>Calculadora de simulação de redução de custos</h2>
      <p>Use o simulador completo abaixo para estimar o impacto no custo por tonelada, na margem e na economia mensal da operação.</p>

      <div style="border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fafafa;max-width:1000px;">
        <iframe
          src="/simulador-custos-cal.html"
          title="Simulador de custos da indústria de cal"
          loading="lazy"
          style="width:100%;height:1450px;border:0;display:block;"
        ></iframe>
      </div>

      <p style="margin-top:10px;font-size:13px;color:#6b7280;"><strong>Privacidade:</strong> os dados inseridos no simulador são processados apenas no seu navegador e <strong>não são armazenados</strong>.</p>
    `,
        category: 'Gestão de Custos',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Cost control in the lime industry: why it defines your margin',
        excerpt:
          'Learn how cost control in fuel, energy, limestone, logistics, and productivity protects margins in lime production — with a practical simulation calculator.',
        content: `
      <h2>Why cost control is critical in the lime industry</h2>
      <p>In lime production, small cost changes per ton can create major impacts on monthly results. Because the process is highly energy-intensive and logistics-dependent, cost management must be continuous and indicator-driven.</p>
      <p>This article includes a practical simulation calculator to estimate potential savings scenarios for operations teams and managers.</p>

      <h2>Cost reduction simulation calculator</h2>
      <p>Use the full simulator below to estimate cost-per-ton impact, margin changes, and monthly savings.</p>

      <div style="border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#fafafa;max-width:1000px;">
        <iframe
          src="/simulador-custos-cal.html"
          title="Lime industry cost simulator"
          loading="lazy"
          style="width:100%;height:1450px;border:0;display:block;"
        ></iframe>
      </div>

      <p style="margin-top:10px;font-size:13px;color:#6b7280;"><strong>Privacy notice:</strong> data entered in this simulator is processed locally in your browser and <strong>is not stored</strong>.</p>
    `,
        category: 'Cost Management',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '1',
    slug: 'cal-biocombustiveis-etanol-biodiesel-biogas',
    image_url: '/media/biocombustivel-cal-usina-acucar-quimical-minasgerais-parana.png',
    published_at: '2026-01-20',
    translations: {
      pt: {
        title: 'Cal nos biocombustíveis: eficiência em etanol, biodiesel e biogás',
        excerpt:
          'Veja como a cal virgem e a cal hidratada ajudam biorrefinarias a corrigir pH, remover impurezas e otimizar rendimento em etanol, biodiesel e biogás.',
        content: `
      <h2>Por que a cal é estratégica na cadeia dos biocombustíveis?</h2>
      <p>A produção de biocombustíveis exige estabilidade de processo, controle rigoroso de pH e redução de contaminantes. <strong>Cal virgem</strong> (CaO) e <strong>cal hidratada</strong> (Ca(OH)₂) são aliadas diretas em etapas críticas das biorrefinarias, do pré-tratamento ao tratamento de efluentes.</p>
      <p>Para usinas de etanol, biodiesel e operações de biogás, o uso correto da cal impacta <strong>rendimento, qualidade do produto</strong> e <strong>conformidade ambiental</strong>.</p>

      <h2>Aplicações da cal em etanol e biorrefinarias</h2>
      <p>Na produção de etanol de cana e milho, a cal é utilizada para:</p>
      <ul>
        <li><strong>Correção de pH</strong> do caldo e do mosto, garantindo condições ideais de fermentação.</li>
        <li><strong>Clarificação</strong> e redução de impurezas, com maior estabilidade microbiológica.</li>
        <li><strong>Tratamento de efluentes</strong> com precipitação de metais e neutralização.</li>
      </ul>
      <p>O resultado é maior eficiência de fermentação e menor variação na qualidade do etanol.</p>

      <h2>Cal no biodiesel: controle de acidez e purificação</h2>
      <p>Na produção de biodiesel, as matérias-primas podem conter ácidos graxos livres e impurezas. A cal atua como agente <strong>neutralizante</strong> e ajuda a reduzir a carga de contaminantes que afetam a transesterificação.</p>
      <p>Isso contribui para:</p>
      <ul>
        <li>Melhor desempenho do catalisador.</li>
        <li>Redução de sabões e emulsões.</li>
        <li>Menor consumo de reagentes corretivos.</li>
      </ul>

      <h2>Biogás e biometano: estabilidade do processo biológico</h2>
      <p>Em biodigestores, a alcalinidade é crucial para manter a atividade dos microrganismos. A cal ajuda a:</p>
      <ul>
        <li>Amortecer oscilações de pH com cargas orgânicas variáveis.</li>
        <li>Reduzir compostos inibidores, como sulfetos.</li>
        <li>Manter estabilidade do processo e produção contínua de biogás.</li>
      </ul>

      <h2>Qual escolher: cal virgem ou cal hidratada?</h2>
      <p>A escolha depende do seu perfil operacional:</p>
      <ul>
        <li><strong>Cal virgem:</strong> maior concentração de óxido ativo e melhor eficiência logística, recomendada para operações de maior escala.</li>
        <li><strong>Cal hidratada:</strong> pronta para uso, mais fácil de manusear e com menor risco térmico.</li>
      </ul>
      <p>A Quimical ajuda a definir a solução técnica ideal para cada etapa do seu processo.</p>

      <h2>Boas práticas na compra de cal para biocombustíveis</h2>
      <ul>
        <li>Exija certificados de pureza e reatividade para evitar instabilidade operacional.</li>
        <li>Avalie <strong>custo por óxido efetivo</strong>, não apenas preço por tonelada.</li>
        <li>Considere logística e armazenamento para evitar hidratação indesejada.</li>
      </ul>

      <h2>Conclusão</h2>
      <p>Em um mercado que exige produtividade e sustentabilidade, a cal é um insumo de alto impacto para o setor de biocombustíveis. A Quimical fornece cal virgem e cal hidratada com controle de qualidade, suporte técnico e logística eficiente para biorrefinarias em todo o Brasil.</p>
    `,
        category: 'Biocombustíveis',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Lime in Biofuels: Efficiency in Ethanol, Biodiesel, and Biogas',
        excerpt:
          'Learn how quicklime and hydrated lime support biorefineries in pH correction, impurity removal, and yield optimization for ethanol, biodiesel, and biogas.',
        content: `
      <h2>Why is lime strategic in the biofuels chain?</h2>
      <p>Biofuel production demands process stability, strict pH control, and contaminant reduction. <strong>Quicklime</strong> (CaO) and <strong>hydrated lime</strong> (Ca(OH)₂) are direct allies in critical biorefinery stages, from pretreatment to effluent treatment.</p>
      <p>For ethanol plants, biodiesel facilities, and biogas operations, proper lime usage impacts <strong>yield, product quality</strong> and <strong>environmental compliance</strong>.</p>

      <h2>Lime applications in ethanol and biorefineries</h2>
      <p>In sugarcane and corn ethanol, lime is used for:</p>
      <ul>
        <li><strong>pH correction</strong> of juice and mash, ensuring ideal fermentation conditions.</li>
        <li><strong>Clarification</strong> and impurity reduction, with greater microbiological stability.</li>
        <li><strong>Effluent treatment</strong> with metal precipitation and neutralization.</li>
      </ul>
      <p>The result is higher fermentation efficiency and less variation in ethanol quality.</p>

      <h2>Lime in biodiesel: acidity control and purification</h2>
      <p>In biodiesel production, feedstocks can contain free fatty acids and impurities. Lime acts as a <strong>neutralizing</strong> agent and helps reduce the contaminant load that affects transesterification.</p>
      <p>This contributes to:</p>
      <ul>
        <li>Better catalyst performance.</li>
        <li>Reduced soaps and emulsions.</li>
        <li>Lower consumption of corrective reagents.</li>
      </ul>

      <h2>Biogas and biomethane: biological process stability</h2>
      <p>In biodigesters, alkalinity is crucial to maintain microorganism activity. Lime helps:</p>
      <ul>
        <li>Buffer pH swings with variable organic loads.</li>
        <li>Reduce inhibitory compounds like sulfides.</li>
        <li>Maintain process stability and continuous biogas production.</li>
      </ul>

      <h2>Which to choose: quicklime or hydrated lime?</h2>
      <p>The choice depends on your operating profile:</p>
      <ul>
        <li><strong>Quicklime:</strong> higher active oxide concentration and more efficient logistics cost, recommended for larger-scale operations.</li>
        <li><strong>Hydrated lime:</strong> ready to use, easier handling, and lower thermal risk.</li>
      </ul>
      <p>Quimical helps define the ideal technical solution for each stage of your process.</p>

      <h2>Best practices for purchasing lime in biofuels</h2>
      <ul>
        <li>Require purity and reactivity certificates to avoid operational instability.</li>
        <li>Evaluate <strong>cost per effective oxide</strong>, not just price per ton.</li>
        <li>Consider logistics and storage to prevent unwanted hydration.</li>
      </ul>

      <h2>Conclusion</h2>
      <p>In a market that demands productivity and sustainability, lime is a high-impact input for the biofuels sector. Quimical supplies quicklime and hydrated lime with quality control, technical support, and efficient logistics for biorefineries across Brazil.</p>
    `,
        category: 'Biofuels',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '2',
    slug: 'municipios-atendidos-distribuicao-fabricantes-brasil',
    image_url: '/media/logistica-caminhao-cal.jpg',
    published_at: '2026-01-16',
    translations: {
      pt: {
        title: 'Municípios atendidos e a importância da distribuição de fabricantes no Brasil',
        excerpt:
          'Conheça os municípios atendidos pela Quimical e entenda por que a distribuição de fabricantes pelo país reduz custos logísticos, melhora prazos e fortalece o atendimento regional.',
        content: `
      <h2>Presença nacional da Quimical: estratégia logística e unidades produtivas</h2>
      <p>Na Quimical, sabemos que para entregar o melhor produto precisamos estar perto de quem consome. Por isso estruturamos nossa operação industrial com uma rede de plantas estrategicamente distribuídas pelo Brasil.</p>
      <p>Diferente de simples escritórios de venda, nossas unidades são onde a “mágica” acontece: produção física e origem dos insumos que movem o mercado. Essa presença geográfica não é um detalhe; é o coração da nossa estratégia para garantir entregas rápidas, custos competitivos e total confiabilidade.</p>

      <h2>Onde estão as fábricas da Quimical?</h2>
      <p>Para atender diferentes regiões com agilidade, nossas unidades industriais estão localizadas nos seguintes estados e municípios:</p>
      <ul>
        <li>Minas Gerais: Arcos, Córrego Fundo, Pains e São José da Lapa.</li>
        <li>Paraná: Colombo, Almirante Tamandaré e Ponta Grossa.</li>
        <li>Bahia: Euclides da Cunha.</li>
        <li>Rio Grande do Norte: Apodi.</li>
      </ul>
      <p>Essa distribuição permite que nossos produtos saiam de polos produtivos muito mais próximos dos centros consumidores, reduzindo distâncias e otimizando o transporte.</p>

      <h2>Por que a descentralização é vantajosa para você?</h2>
      <p>Em um país com dimensões continentais como o Brasil, depender de uma única fábrica pode gerar atrasos e fretes elevados. Ao descentralizar nossa produção, oferecemos três grandes vantagens competitivas para o seu negócio:</p>
      <ol>
        <li>
          <strong>Agilidade e redução de custos</strong><br />
          A proximidade reduz o tempo de viagem das cargas. Isso significa menor consumo de combustível e menor desgaste da frota, impactando diretamente o custo final do produto. Além disso, prazos menores e mais previsíveis permitem operar com estoques enxutos, liberando capital de giro.
        </li>
        <li>
          <strong>Segurança de fornecimento e confiabilidade</strong><br />
          Eventos inesperados acontecem, seja por clima ou problemas rodoviários. Com várias unidades fabris, a Quimical tem flexibilidade operacional. Se uma região enfrenta dificuldades, outra unidade atende a demanda, garantindo que sua produção não pare.
        </li>
        <li>
          <strong>Sustentabilidade real</strong><br />
          Eficiência logística também é responsabilidade ambiental. Ao reduzir as distâncias percorridas por caminhões, diminuímos significativamente as emissões de CO₂ por tonelada transportada. Escolher a Quimical é optar por uma cadeia de suprimentos mais limpa e eficiente.
        </li>
      </ol>

      <h2>Otimização Quimical: compromisso com a eficiência</h2>
      <p>Nossa estrutura vai além da fabricação. Ao posicionar nossas plantas de forma inteligente, alinhamos desempenho econômico e responsabilidade ambiental. O resultado é uma parceria sólida, capaz de atender projetos urgentes e variações de mercado com a robustez que sua empresa precisa.</p>
    `,
        category: 'Logística & Distribuição',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Served Municipalities and the Importance of Manufacturer Distribution in Brazil',
        excerpt:
          'Discover the municipalities served by Quimical and understand why distributing manufacturers across the country reduces logistics costs, improves lead times, and strengthens regional service.',
        content: `
      <h2>Quimical's National Presence: Logistics Strategy and Production Units</h2>
      <p>At Quimical, we know that to deliver the best product, we must be close to those who consume it. That's why we structure our industrial operation with a network of production plants strategically distributed across Brazil.</p>
      <p>Unlike simple sales offices, our units are where the "magic" happens: physical production and the origin of the inputs that move the market. This geographic presence is not just a detail; it's the heart of our strategy to ensure faster deliveries, competitive costs, and total reliability.</p>

      <h2>Where are Quimical's factories?</h2>
      <p>To serve different regions quickly, our industrial units are located in the following states and municipalities:</p>
      <ul>
        <li>Minas Gerais: Arcos, Córrego Fundo, Pains, and São José da Lapa.</li>
        <li>Paraná: Colombo, Almirante Tamandaré, and Ponta Grossa.</li>
        <li>Bahia: Euclides da Cunha.</li>
        <li>Rio Grande do Norte: Apodi.</li>
      </ul>
      <p>This distribution allows our products to leave production hubs much closer to consumer centers, shortening distances and optimizing transport.</p>

      <h2>Why is decentralization advantageous for you?</h2>
      <p>In a country with continental dimensions like Brazil, relying on a single factory can generate delays and high freight costs. By decentralizing our production, we offer three major competitive advantages for your business:</p>
      <ol>
        <li>
          <strong>Agility and Cost Reduction</strong><br />
          Proximity reduces travel time for cargo. This means lower fuel consumption and less wear on the fleet, directly impacting the final cost of the product. In addition, shorter and more predictable lead times allow your company to operate with lean inventories, freeing up working capital.
        </li>
        <li>
          <strong>Supply Security and Reliability</strong><br />
          Unexpected events happen, whether due to weather or road issues. With multiple manufacturing units, Quimical has operational flexibility. If one region faces difficulties, another unit can meet demand, ensuring your production doesn't stop.
        </li>
        <li>
          <strong>Real Sustainability</strong><br />
          Logistics efficiency is also environmental responsibility. By reducing the distances traveled by trucks, we significantly cut CO₂ emissions per ton transported. Choosing Quimical means choosing a cleaner and more efficient supply chain.
        </li>
      </ol>

      <h2>Quimical Optimization: Commitment to Efficiency</h2>
      <p>Our structure goes beyond manufacturing. By positioning our plants intelligently, we align economic performance and environmental responsibility. The result is a solid partnership, capable of meeting urgent projects and market variations with the robustness your company needs.</p>
    `,
        category: 'Logistics & Distribution',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '3',
    slug: 'como-otimizar-compra-cal-industrial',
    image_url: '/media/comprasindustriais.jpg',
    published_at: '2026-01-14',
    translations: {
      pt: {
        title: 'Como comprar cal industrial: guia de cotação e avaliação de fornecedor',
        excerpt:
          'Aprenda a avaliar fornecedores de cal, comparar custo por óxido efetivo e garantir o melhor custo-benefício na compra de cal virgem e cal hidratada.',
        content: `
      <h2>Por que estratégia na compra de cal reduz custos</h2>
      <p><strong>Cal industrial</strong> é um insumo crítico para siderurgia, papel e celulose e tratamento de efluentes. Porém, um erro comum é olhar apenas o <strong>preço por tonelada de cal</strong> e ignorar variáveis que impactam o orçamento final. Comprar sem análise técnica pode gerar perdas operacionais e queda na qualidade do produto final.</p>
      
      <p>Neste guia da Quimical, apresentamos uma metodologia para otimizar suas compras, com foco em eficiência química, logística e custo total de propriedade (TCO).</p>
      
      <h2>Custo da cal: preço nominal vs. óxido efetivo</h2>
      <p>O valor da nota fiscal é apenas o começo. O indicador real de economia é o <strong>custo por quilograma de óxido ativo</strong> (CaO para cal calcítica ou MgO para cal dolomítica). Produtos de baixa pureza parecem mais baratos, mas exigem maior volume para o mesmo efeito químico.</p>
      
      <p><em>Exemplo prático:</em> Cal A (92% CaO) custa R$ 500/ton. Cal B (85% CaO) custa R$ 450/ton. Embora a Cal B pareça mais barata, o custo por kg de óxido efetivo da Cal A é praticamente o mesmo, mas ela gera menos resíduo e exige menos frete. No final, <strong>cal de alta pureza</strong> entrega economia real.</p>
      
      <h2>Critérios para avaliar fornecedores de cal</h2>
      <h3>1. Consistência e relatórios técnicos</h3>
      <p>Variação entre lotes desestabiliza a indústria. Exija do seu <strong>fornecedor de cal</strong> relatórios históricos e desvio padrão dos teores. Empresas certificadas (ISO 9001) mantêm controle estatístico rigoroso, garantindo que o teor contratado é o que é entregue.</p>
      
      <h3>2. Granulometria e reatividade</h3>
      <p>Granulometria incorreta impacta a reação no processo. Na siderurgia, excesso de finos se perde por arraste. Além disso, a <strong>reatividade da cal virgem</strong> (tempo de hidratação) define a velocidade do processo. Para dessulfuração, por exemplo, alta reatividade é obrigatória.</p>
      
      <h2>Calculadora de custo-benefício da cal</h2>
      <p>Não baseie sua decisão apenas no preço de tabela. Use nossa ferramenta para comparar o rendimento químico das propostas:</p>
      
      <div id="cal-calculator-placeholder">
        <p>Compare fornecedores: calcule o custo real por tonelada de óxido ativo.</p>
      </div>
      
      <h2>Logística e frete da cal em Minas Gerais e no Brasil</h2>
      <p>O frete pode representar até 40% do custo. Localizada em Arcos/MG, o maior polo produtor do Brasil, a Quimical tem uma posição estratégica para reduzir custos logísticos para múltiplas regiões. Avalie também o modal de envio: <strong>cal a granel</strong> reduz embalagens, enquanto <strong>cal ensacada (big bag)</strong> oferece flexibilidade sem silos.</p>
      
      <h2>Conclusão</h2>
      <p>Otimizar a compra de cal exige olhar o "Custo Total". A Quimical atua como parceira técnica, ajudando sua indústria a especificar o produto exato — cal virgem ou cal hidratada — para garantir máximo desempenho com o menor custo total.</p>
    `,
        category: 'Compras Industriais',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'How to Buy Industrial Lime: Quote and Supplier Evaluation Guide',
        excerpt:
          'Learn how to evaluate lime suppliers, compare price per effective oxide, and secure the best cost-benefit when buying quicklime and hydrated lime.',
        content: `
      <h2>Why Strategy in Lime Purchasing Reduces Costs</h2>
      <p><strong>Industrial lime</strong> is a critical input for sectors such as steelmaking, pulp and paper, and effluent treatment. However, a common mistake among buyers is focusing only on the <strong>price per ton of lime</strong>, ignoring variables that affect the final budget. A purchase without technical analysis can lead to operational losses and a drop in final product quality.</p>
      
      <p>In this Quimical guide, we present a methodology to optimize your purchases, focusing on chemical efficiency, logistics, and total cost of ownership (TCO).</p>
      
      <h2>Lime Cost: Nominal Price vs. Effective Oxide</h2>
      <p>The invoice value is just the beginning. The real savings indicator is the <strong>cost per kilogram of active oxide</strong> (either CaO for calcitic lime or MgO for dolomitic lime). Low-purity products appear cheaper but require higher volumes for the same chemical effect.</p>
      
      <p><em>Practical Example:</em> Lime A (92% CaO) costs R$ 500/ton. Lime B (85% CaO) costs R$ 450/ton. Although Lime B seems cheaper, the cost per kg of effective oxide for Lime A is practically the same, but it generates less residue and requires less freight. In the end, <strong>high-purity lime</strong> delivers real savings.</p>
      
      <h2>Criteria to Evaluate Lime Suppliers</h2>
      <h3>1. Consistency and Technical Reports</h3>
      <p>Quality variation between batches destabilizes industry. Require your <strong>lime supplier</strong> to provide historical reports and standard deviation of contents. Certified companies (ISO 9001) maintain rigorous statistical control, ensuring the contracted oxide content is what is delivered.</p>
      
      <h3>2. Particle Size and Reactivity</h3>
      <p>Incorrect particle size affects the reaction in the process. In steelmaking, excessive fines are lost by carryover. In addition, <strong>quicklime reactivity</strong> (slaking time) defines process speed. For desulfurization, for example, high reactivity is mandatory.</p>
      
      <h2>Lime Cost-Benefit Calculator</h2>
      <p>Do not base your decision only on list price. Use our tool to compare the chemical yield of proposals:</p>
      
      <div id="cal-calculator-placeholder">
        <p>Compare suppliers: Calculate the real cost per ton of active oxide.</p>
      </div>
      
      <h2>Logistics and Lime Freight in Minas Gerais and Brazil</h2>
      <p>Freight can represent up to 40% of cost. Located in Arcos/MG, the largest production hub in Brazil, Quimical has a strategic position to reduce logistics costs for multiple regions. Also evaluate the shipping mode: <strong>bulk lime</strong> saves on packaging, while <strong>bagged lime (big bag)</strong> offers flexibility without silos.</p>
      
      <h2>Conclusion</h2>
      <p>Optimizing lime purchasing requires looking at the "Total Cost." Quimical acts as a technical partner, helping your industry specify the exact product — whether quicklime or hydrated lime — to ensure maximum performance at the lowest total cost.</p>
    `,
        category: 'Industrial Purchasing',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '4',
    slug: 'cal-virgem-siderurgia-metalurgia',
    image_url: '/media/quicklime-calcium-oxide.jpg',
    published_at: '2025-03-22',
    translations: {
      pt: {
        title: 'Cal virgem na siderurgia: especificações para dessulfuração e escória',
        excerpt:
          'Entenda o papel da cal virgem calcítica e dolomítica na siderurgia. Veja como reatividade e pureza impactam dessulfuração e refino do aço.',
        content: `
      <h2>Cal como insumo crítico na siderurgia</h2>
      <p>A indústria do aço é a maior consumidora mundial de <strong>cal virgem</strong>. Do alto-forno ao refino secundário, a qualidade da cal (CaO) determina eficiência energética e limpeza do aço. Especificações técnicas rigorosas são a barreira contra perdas de produtividade.</p>
      
      <p>Para compradores e engenheiros metalúrgicos, buscar fornecedores que garantam teores de CaO acima de 90% e baixo enxofre residual é vital para evitar retrabalho no refino.</p>
      
      <h2>Papel da cal no alto-forno e aciaria</h2>
      <p>No alto-forno, a cal atua como fundente, controlando a basicidade da escória e removendo sílica. Cal com granulometria estável (ex.: 10-40mm) garante permeabilidade da carga. Na aciaria, a pureza química é o fator predominante.</p>
      
      <h2>Dessulfuração do gusa e do aço</h2>
      <p>Controlar o enxofre é o maior desafio. <strong>Cal virgem altamente reativa</strong> é o principal agente dessulfurante. Ela precisa dissolver rapidamente na escória para capturar o enxofre. Cal “superqueimada” ou de baixa reatividade aumenta o tempo de corrida e o consumo de eletrodos e energia.</p>
      
      <h2>Cal calcítica vs. dolomítica na siderurgia</h2>
      <p>Enquanto a <strong>cal calcítica</strong> foca na limpeza do metal, a <strong>cal dolomítica</strong> (rica em MgO) é essencial para proteger os refratários do forno, aumentando a vida útil do revestimento (campanha do forno). O equilíbrio ideal entre CaO e MgO reduz custos de manutenção.</p>
      
      <h2>Especificações técnicas ideais</h2>
      <p>Para garantir performance siderúrgica, a Quimical recomenda os seguintes parâmetros de referência:</p>
      <ul>
        <li><strong>CaO (Óxido de cálcio):</strong> > 90% (Ideal > 92%)</li>
        <li><strong>Sílica (SiO2):</strong> < 1,5% (sílica consome cal, reduzindo eficiência)</li>
        <li><strong>Enxofre (S):</strong> < 0,05%</li>
        <li><strong>Reatividade:</strong> T60 < 3 min (teste de hidratação)</li>
      </ul>
      
      <h2>Impacto da qualidade no custo do aço</h2>
      <p>Usar cal barata e impura torna o aço mais caro. Aumento de volume de escória, perda de metal e desgaste de refratários custam muito mais do que a diferença de preço do insumo. Use nossa calculadora para verificar o impacto da pureza:</p>
      
      <div id="cal-calculator-placeholder">
        <p>Calcule: custo efetivo da cal por tonelada de aço produzida.</p>
      </div>
      
      <h2>Armazenagem e cuidados</h2>
      <p>Cal para siderurgia costuma ser entregue a granel. O gerenciamento de silos e a prevenção de hidratação indesejada (umidade) são essenciais para manter a reatividade até a injeção ou adição no forno.</p>
      
      <h2>Conclusão</h2>
      <p>A Quimical entende de metalurgia. Nossa cal virgem é produzida em Arcos/MG com controle rigoroso para atender as usinas mais exigentes, garantindo dessulfuração eficiente e proteção dos ativos industriais.</p>
    `,
        category: 'Siderurgia',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Quicklime in Steelmaking: Specifications for Desulfurization and Slag',
        excerpt:
          'Understand the role of calcitic and dolomitic quicklime in steelmaking. Learn how reactivity and purity impact desulfurization and steel refining.',
        content: `
      <h2>Lime as a Critical Input in Steelmaking</h2>
      <p>The steel industry is the world's largest consumer of <strong>quicklime</strong>. From the blast furnace to secondary refining, lime quality (CaO) determines energy efficiency and steel cleanliness. Rigorous technical specifications are the barrier against productivity loss.</p>
      
      <p>For buyers and metallurgical engineers, seeking suppliers that guarantee CaO contents above 90% and low residual sulfur is vital to avoid rework in refining.</p>
      
      <h2>Lime's Role in the Blast Furnace and Steel Shop</h2>
      <p>In the blast furnace, lime acts as a flux, controlling slag basicity and removing silica. Lime with stable particle size (e.g., 10-40mm) ensures burden permeability. In the steel shop, chemical purity is the predominant factor.</p>
      
      <h2>Hot Metal and Steel Desulfurization</h2>
      <p>Controlling sulfur is the biggest challenge. <strong>Highly reactive quicklime</strong> is the main desulfurizing agent. It needs to dissolve quickly in the slag to capture sulfur. "Overburned" or low-reactivity limes increase tap-to-tap time and consumption of electrodes and energy.</p>
      
      <h2>Calcitic vs. Dolomitic Lime in Steelmaking</h2>
      <p>While <strong>calcitic lime</strong> focuses on metal cleanliness, <strong>dolomitic lime</strong> (rich in MgO) is essential to protect furnace refractories, increasing lining life (furnace campaign). The ideal balance between CaO and MgO reduces maintenance costs.</p>
      
      <h2>Ideal Technical Specifications</h2>
      <p>To ensure steelmaking performance, Quimical recommends the following reference parameters:</p>
      <ul>
        <li><strong>CaO (Calcium Oxide):</strong> > 90% (Ideal > 92%)</li>
        <li><strong>Silica (SiO2):</strong> < 1.5% (Silica consumes lime, reducing efficiency)</li>
        <li><strong>Sulfur (S):</strong> < 0.05%</li>
        <li><strong>Reactivity:</strong> T60 < 3 min (Slaking test)</li>
      </ul>
      
      <h2>Quality Impact on Steel Cost</h2>
      <p>Using cheap and impure lime makes steel more expensive. Increased slag volume, metal loss, and refractory wear cost far more than the price difference in the input. Use our calculator to check the impact of purity:</p>
      
      <div id="cal-calculator-placeholder">
        <p>Calculate: Effective lime cost per ton of steel produced.</p>
      </div>
      
      <h2>Storage and Care</h2>
      <p>Steelmaking lime is typically delivered in bulk. Silo management and prevention of unintended hydration (moisture) are essential to maintain product reactivity until injection or addition in the furnace.</p>
      
      <h2>Conclusion</h2>
      <p>Quimical understands metallurgy. Our quicklime is produced in Arcos/MG with rigorous control to serve the most demanding steel plants, ensuring efficient desulfurization and protection of your industrial assets.</p>
    `,
        category: 'Steelmaking',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '5',
    slug: 'cal-virgem-vs-cal-hidratada-industria',
    image_url: '/media/calvirgemoucalhidratada.jpg',
    published_at: '2025-04-07',
    translations: {
      pt: {
        title: 'Cal virgem ou cal hidratada? Diferenças e como escolher para sua indústria',
        excerpt:
          'Cal virgem (CaO) ou cal hidratada (Ca(OH)₂)? Veja a comparação técnica de custos, reatividade e aplicações para decidir o melhor produto.',
        content: `
      <h2>CaO vs. Ca(OH)₂: entendendo as diferenças</h2>
      <p>Uma dúvida comum na indústria é escolher entre <strong>cal virgem</strong> (óxido de cálcio) e <strong>cal hidratada</strong> (hidróxido de cálcio). Embora venham do mesmo calcário, são produtos quimicamente distintos e com aplicações e custos logísticos diferentes.</p>
      
      <p>Esta análise técnica esclarece qual tipo de cal oferece o melhor TCO (Custo Total de Propriedade) para o seu processo, seja tratamento de água, construção civil ou indústria química.</p>
      
      <h2>O que é cal virgem (óxido de cálcio)?</h2>
      <p>Produzida pela calcinação do calcário em altas temperaturas, a cal virgem é anidra. Ela é altamente concentrada e reage de forma exotérmica com a água.</p>
      
      <h3>Vantagens da cal virgem:</h3>
      <ul>
        <li><strong>Eficiência logística:</strong> 1 tonelada de cal virgem equivale a cerca de 1,3 tonelada de cal hidratada em poder neutralizante. Você transporta menos “água”.</li>
        <li><strong>Custo:</strong> Geralmente mais barata por kg de óxido ativo.</li>
        <li><strong>Geração de calor:</strong> Útil para secagem de lodos e reações térmicas.</li>
      </ul>
      
      <h3>Pontos de atenção:</h3>
      <p>Requer equipamento de hidratação (apagamento) na planta e cuidados extras de segurança (EPI) por causa da alta temperatura da reação.</p>
      
      <h2>O que é cal hidratada (hidróxido de cálcio)?</h2>
      <p>É a cal virgem que já passou pela hidratação industrial. É um pó fino, estável e pronto para uso.</p>
      
      <h3>Vantagens da cal hidratada:</h3>
      <ul>
        <li><strong>Praticidade:</strong> Não exige hidratador na planta do cliente.</li>
        <li><strong>Segurança:</strong> Não gera calor quando misturada com água.</li>
        <li><strong>Dosagem:</strong> Fácil em processos que exigem alta precisão de pH ou suspensões finas (leite de cal).</li>
      </ul>
      
      <h2>Comparativo de aplicações</h2>
      <h3>Quando usar cal virgem?</h3>
      <ul>
        <li>Grandes volumes de consumo (siderurgia, papel e celulose).</li>
        <li>Tratamento de efluentes com alta acidez.</li>
        <li>Estabilização de solos em rodovias (secagem).</li>
      </ul>
      
      <h3>Quando usar cal hidratada?</h3>
      <ul>
        <li>Construção civil (argamassas e rebocos).</li>
        <li>Tratamento de água potável (escala menor).</li>
        <li>Indústrias sem infraestrutura de hidratação.</li>
        <li>Indústrias alimentícia e farmacêutica.</li>
      </ul>
      
      <h2>Conversão e calculadora de custos</h2>
      <p>Ainda em dúvida sobre o que é mais econômico considerando o frete? Use nossa ferramenta:</p>
      
      <div id="cal-calculator-placeholder">
        <p>Simulador: Custo efetivo Cal Virgem vs. Cal Hidratada.</p>
      </div>
      
      <h2>Segurança no manuseio</h2>
      <p>A Quimical reforça: ambos são produtos químicos alcalinos. EPI é obrigatório. A cal virgem, especificamente, nunca deve ser armazenada em locais com risco de umidade para evitar reações acidentais.</p>
      
      <h2>Conclusão</h2>
      <p>Não existe “melhor cal”, existe a ideal para sua estrutura. Se você tem capacidade de hidratação, a cal virgem traz economia. Se busca praticidade imediata e segurança, a cal hidratada é a solução. A Quimical fornece ambos, com garantia de qualidade da produção em Arcos/MG.</p>
    `,
        category: 'Produtos',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Quicklime or Hydrated Lime? Differences and Which to Choose for Industry',
        excerpt:
          'Quicklime (CaO) or Hydrated (Ca(OH)2)? See a technical comparison of costs, reactivity, and applications to decide the best product for your company.',
        content: `
      <h2>CaO vs. Ca(OH)₂: Understanding the Differences</h2>
      <p>A common industrial question is choosing between <strong>quicklime</strong> (calcium oxide) and <strong>hydrated lime</strong> (calcium hydroxide). Although they come from the same limestone, they are chemically distinct products with different applications and logistics costs.</p>
      
      <p>This technical analysis clarifies which type of lime offers the best TCO (Total Cost of Ownership) for your process, whether it's water treatment, construction, or chemical production.</p>
      
      <h2>What is Quicklime (Calcium Oxide)?</h2>
      <p>Produced by calcining limestone at high temperatures, quicklime is anhydrous. It is highly concentrated and reacts exothermically with water.</p>
      
      <h3>Advantages of Quicklime:</h3>
      <ul>
        <li><strong>Logistics Efficiency:</strong> 1 ton of quicklime equals about 1.3 tons of hydrated lime in neutralizing power. You transport less "water."</li>
        <li><strong>Cost:</strong> Generally cheaper per kg of active oxide.</li>
        <li><strong>Heat Generation:</strong> Useful for sludge drying and thermal reactions.</li>
      </ul>
      
      <h3>Points to Consider:</h3>
      <p>It requires hydration (slaking) equipment on site and extra safety precautions (PPE) due to the high reaction temperature.</p>
      
      <h2>What is Hydrated Lime (Calcium Hydroxide)?</h2>
      <p>It is quicklime that has already undergone industrial hydration. It is a fine, stable powder ready for use.</p>
      
      <h3>Advantages of Hydrated Lime:</h3>
      <ul>
        <li><strong>Practicality:</strong> Does not require a slaker at the customer's plant.</li>
        <li><strong>Safety:</strong> Does not generate heat when mixed with water.</li>
        <li><strong>Dosing:</strong> Easy in processes that require high pH precision or fine suspensions (lime milk).</li>
      </ul>
      
      <h2>Application Comparison</h2>
      <h3>When to use Quicklime?</h3>
      <ul>
        <li>Large consumption volumes (Steelmaking, Pulp and Paper).</li>
        <li>Effluent treatment with high acidity.</li>
        <li>Soil stabilization in highways (drying).</li>
      </ul>
      
      <h3>When to use Hydrated Lime?</h3>
      <ul>
        <li>Civil Construction (Mortars and Plasters).</li>
        <li>Drinking water treatment (smaller scale).</li>
        <li>Industries without slaking infrastructure.</li>
        <li>Food and Pharmaceutical industries.</li>
      </ul>
      
      <h2>Conversion and Cost Calculator</h2>
      <p>Still unsure which is more economical considering freight? Use our tool:</p>
      
      <div id="cal-calculator-placeholder">
        <p>Simulator: Effective Cost Quicklime vs. Hydrated Lime.</p>
      </div>
      
      <h2>Handling Safety</h2>
      <p>Quimical reinforces: both are alkaline chemical products. PPE is mandatory. Quicklime, specifically, should never be stored in locations with moisture risk to avoid accidental reactions.</p>
      
      <h2>Conclusion</h2>
      <p>There is no "best" lime, only the ideal one for your structure. If you have hydration capacity, quicklime brings savings. If you seek immediate practicality and safety, hydrated lime is the solution. Quimical supplies both, with the quality guarantee of production in Arcos/MG.</p>
    `,
        category: 'Products',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '6',
    slug: 'cal-tratamento-efluentes-industriais',
    image_url: '/media/tratamentodeaguaquimical.jpg',
    published_at: '2025-02-18',
    translations: {
      pt: {
        title: 'Cal no tratamento de efluentes: correção de pH e remoção de metais',
        excerpt:
          'Saiba como a cal virgem e a cal hidratada tratam efluentes industriais, precipitam metais pesados e garantem conformidade ambiental (CONAMA).',
        content: `
      <h2>Solução econômica para efluentes industriais</h2>
      <p>O tratamento de efluentes é uma exigência legal e ambiental. Entre os reagentes químicos disponíveis, <strong>cal (virgem ou hidratada)</strong> se destaca como a melhor opção de custo-benefício para <strong>correção de pH</strong> e remoção de poluentes inorgânicos.</p>
      
      <p>Setores como mineração, galvanoplastia e têxtil dependem da cal para garantir que o descarte esteja dentro das normas do CONAMA e evitar multas ambientais severas.</p>
      
      <h2>Funções da cal na ETE</h2>
      <h3>1. Neutralização da acidez (pH)</h3>
      <p>Efluentes ácidos corroem equipamentos e são proibidos de serem descartados. A cal eleva o pH de forma segura e mais barata do que a soda cáustica. Além disso, o cálcio residual é menos nocivo ao meio ambiente do que o sódio.</p>
      
      <h3>2. Precipitação de metais pesados</h3>
      <p>Metais como Cromo, Níquel, Zinco e Chumbo são solúveis em meio ácido. Ao elevar o pH com a cal, formam-se hidróxidos metálicos insolúveis que precipitam e geram lodo, permitindo a separação de água limpa.</p>
      
      <h3>3. Remoção de fosfatos e fluoretos</h3>
      <p>Essencial para indústrias de fertilizantes e alumínio. O cálcio reage formando fosfato de cálcio ou fluoreto de cálcio, sólidos estáveis facilmente removidos por decantação.</p>
      
      <h3>4. Condicionamento de lodo</h3>
      <p>A cal não apenas trata a água, mas melhora o lodo. Ela ajuda na desidratação em filtros prensa e reduz odores desagradáveis ao inibir a atividade bacteriana putrefativa.</p>
      
      <h2>Vantagens da cal sobre a soda cáustica</h2>
      <ul>
        <li><strong>Custo:</strong> A cal pode ser de 3 a 4 vezes mais barata do que a soda para o mesmo poder neutralizante.</li>
        <li><strong>Sedimentação:</strong> A cal auxilia a floculação, produzindo lodo mais denso e fácil de decantar.</li>
        <li><strong>Segurança ambiental:</strong> Menor risco de salinização de corpos d’água receptores.</li>
      </ul>
      
      <h2>Dimensionamento da dosagem</h2>
      <p>A escolha entre usar <strong>Leite de Cal</strong> (suspensão líquida) ou dosagem a seco depende do porte da ETE. O controle automático de pH é crucial para evitar superdosagem e desperdício.</p>
      
      <p>Quer saber quanto pode economizar ao substituir seu neutralizante atual por cal de alta pureza?</p>
      
      <div id="cal-calculator-placeholder">
        <p>Calculadora: custo do tratamento de efluentes com cal Quimical.</p>
      </div>
      
      <h2>Gestão de lodo e resíduos</h2>
      <p>O lodo gerado com cal tende a ser mais estável. Em muitos casos, se devidamente classificado (Classe II), tem menor custo de disposição. A alcalinidade residual evita a re-solubilização de metais.</p>
      
      <h2>Conclusão</h2>
      <p>Para conformidade ambiental com baixo custo operacional, a cal é imbatível. A Quimical oferece suporte técnico para ajustar o pH da sua ETE e fornece produtos específicos para saneamento e tratamento industrial.</p>
    `,
        category: 'Meio Ambiente',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Lime in Effluent Treatment: pH Correction and Metal Removal',
        excerpt:
          'Learn how to use quicklime and hydrated lime to treat industrial effluents, precipitate heavy metals, and bring your company into environmental compliance (CONAMA).',
        content: `
      <h2>Economical Solution for Industrial Effluents</h2>
      <p>Effluent treatment is a legal and environmental requirement. Among available chemical reagents, <strong>lime (quick or hydrated)</strong> stands out as the best cost-benefit option for <strong>pH correction</strong> and removal of inorganic pollutants.</p>
      
      <p>Sectors such as mining, electroplating, and textiles depend on lime to ensure discharge meets CONAMA standards and avoids heavy environmental fines.</p>
      
      <h2>Lime Functions in the Treatment Plant (ETP)</h2>
      <h3>1. Acidity Neutralization (pH)</h3>
      <p>Acidic effluents corrode equipment and are prohibited from being discharged. Lime raises pH safely and more cheaply than caustic soda. In addition, residual calcium is less harmful to the environment than sodium.</p>
      
      <h3>2. Heavy Metal Precipitation</h3>
      <p>Metals such as Chromium, Nickel, Zinc, and Lead are soluble in acidic media. By raising pH with lime, insoluble metal hydroxides form, precipitating and forming sludge, allowing separation of clean water.</p>
      
      <h3>3. Phosphate and Fluoride Removal</h3>
      <p>Essential for fertilizer and aluminum industries. Calcium reacts forming calcium phosphate or calcium fluoride, stable solids that are easily removed by settling.</p>
      
      <h3>4. Sludge Conditioning</h3>
      <p>Lime not only treats water but improves sludge. It helps dewater (dry) sludge in filter presses and reduces unpleasant odors by inhibiting putrefactive bacterial activity.</p>
      
      <h2>Advantages of Lime over Caustic Soda</h2>
      <ul>
        <li><strong>Cost:</strong> Lime can be 3 to 4 times cheaper than soda for the same neutralizing power.</li>
        <li><strong>Sedimentation:</strong> Lime aids flocculation, producing denser sludge that is easier to settle.</li>
        <li><strong>Environmental Safety:</strong> Lower risk of salinization of receiving water bodies.</li>
      </ul>
      
      <h2>Dosage Sizing</h2>
      <p>The choice between using <strong>Lime Milk</strong> (liquid suspension) or dry dosing depends on the size of the ETP. Automated pH control is crucial to avoid overdosing and waste.</p>
      
      <p>Want to know how much you can save by switching your current neutralizer to high-purity lime?</p>
      
      <div id="cal-calculator-placeholder">
        <p>Calculator: Effluent treatment cost with Quimical Lime.</p>
      </div>
      
      <h2>Sludge and Residue Management</h2>
      <p>Sludge generated with lime is generally more stable. In many cases, if properly classified (Class II), it has lower disposal costs. Residual alkalinity prevents metal re-solubilization.</p>
      
      <h2>Conclusion</h2>
      <p>For environmental compliance with low operating cost, lime is unbeatable. Quimical provides technical support to adjust your ETP pH and supplies specific products for sanitation and industrial treatment.</p>
    `,
        category: 'Environment',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '7',
    slug: 'cal-hidratada-construcao-civil',
    image_url: '/media/calpararebocoquimical.jpeg',
    published_at: '2025-05-30',
    translations: {
      pt: {
        title: 'Cal hidratada na construção civil: argamassas e rebocos duráveis',
        excerpt:
          'Por que usar cal na argamassa? Entenda os benefícios da cal hidratada (CH-I, CH-III) para trabalhabilidade, aderência e prevenção de fissuras.',
        content: `
      <h2>O segredo de obras duráveis</h2>
      <p><strong>Cal hidratada</strong> é insubstituível na construção civil. Apesar de aditivos químicos modernos, a cal continua sendo a melhor opção para garantir “liga”, trabalhabilidade e saúde da alvenaria. Obras que usam cal na argamassa sofrem menos com fissuras e infiltrações.</p>
      
      <p>Entender o papel da cal em argamassas de assentamento e revestimento é vital para engenheiros, mestres de obras e pedreiros que valorizam a qualidade final.</p>
      
      <h2>Vantagens técnicas da cal na argamassa</h2>
      <h3>1. Trabalhabilidade e “liga”</h3>
      <p>A cal atua como lubrificante microscópico. Ela permite que a argamassa deslize melhor na colher e na parede, aumentando a produtividade da mão de obra. Uma argamassa com cal é mais “gorda” e fácil de espalhar.</p>
      
      <h3>2. Retenção de água (cura do cimento)</h3>
      <p>Tijolos e blocos puxam água da argamassa. Se isso ocorre rápido demais, o cimento não hidrata (“queima”) e a mistura se esfarela. A cal retém água e a libera gradualmente para o cimento, garantindo maior aderência e resistência.</p>
      
      <h3>3. Elasticidade e prevenção de fissuras</h3>
      <p>Edificações se movimentam (dilatação térmica). Argamassas só com cimento são rígidas e fissuram. A cal oferece elasticidade (menor módulo de elasticidade), permitindo que as paredes absorvam pequenas deformações sem rachar.</p>
      
      <h3>4. Poder bactericida e fungicida</h3>
      <p>O alto pH da cal combate naturalmente mofo, bolor e formação de bactérias, garantindo ambientes internos mais saudáveis.</p>
      
      <h2>Tipos de cal e aplicações (NBR 7175)</h2>
      <p>Sempre use cal certificada. As classificações mais comuns são:</p>
      <ul>
        <li><strong>Argamassa de assentamento:</strong> Traços como 1:1:6 (Cimento:Cal:Areia) oferecem equilíbrio ideal.</li>
        <li><strong>Reboco e emboço:</strong> A cal garante impermeabilidade e acabamento liso.</li>
        <li><strong>Acabamento fino:</strong> Para superfícies refinadas antes da pintura.</li>
      </ul>
      
      <h2>Custo-benefício na obra</h2>
      <p>Muitos tentam “economizar” retirando a cal e usando apenas aditivos plastificantes líquidos. O resultado costuma ser paredes com fissuras e infiltrações após alguns anos. A cal representa pequena fração do custo da obra, mas protege o maior patrimônio: a estrutura.</p>
      
      <div id="cal-calculator-placeholder">
        <p>Calcule a quantidade de cal necessária para sua argamassa.</p>
      </div>
      
      <h2>Dicas de aplicação Quimical</h2>
      <p>Para melhor desempenho, deixe a argamassa “descansar” (maturar) por 10 a 15 minutos antes da aplicação. Isso garante a ativação completa das propriedades da cal hidratada.</p>
      
      <h2>Conclusão</h2>
      <p>Não troque o certo pelo duvidoso. A cal hidratada Quimical, produzida com a pureza do calcário de Minas Gerais, garante que seu reboco e assentamento tenham qualidade profissional e durabilidade por gerações.</p>
    `,
        category: 'Construção Civil',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Hydrated Lime in Civil Construction: Durable Mortars and Plasters',
        excerpt:
          'Why use lime in mortar? Understand the benefits of hydrated lime (CH-I, CH-III) for workability, adhesion, and crack prevention on site.',
        content: `
      <h2>The Secret to Durable Builds</h2>
      <p><strong>Hydrated lime</strong> is irreplaceable in civil construction. Despite modern chemical additives, lime remains the best option to ensure "bond," workability, and wall health. Projects that use lime in mortar suffer less from cracks and infiltration.</p>
      
      <p>Understanding lime's role in laying and plaster mortars is vital for engineers, foremen, and masons who value final quality.</p>
      
      <h2>Technical Advantages of Lime in Mortar</h2>
      <h3>1. Workability and "Bond"</h3>
      <p>Lime works as a microscopic lubricant. It allows mortar to slide better on the trowel and wall, increasing labor productivity. A lime mortar is "fatter" and easier to spread.</p>
      
      <h3>2. Water Retention (Cement Curing)</h3>
      <p>Bricks and blocks draw water from mortar. If that happens too quickly, cement does not hydrate ("burns") and the mix crumbles. Lime retains water, releasing it gradually to the cement, ensuring superior adhesion and strength.</p>
      
      <h3>3. Elasticity and Crack Prevention</h3>
      <p>Buildings move (thermal expansion). Cement-only mortars are rigid and crack. Lime provides elasticity (lower modulus of elasticity), allowing walls to absorb small deformations without cracking.</p>
      
      <h3>4. Bactericidal and Fungicidal Power</h3>
      <p>Lime's high pH naturally combats mold, mildew, and bacteria formation, ensuring healthier indoor environments.</p>
      
      <h2>Types of Lime and Applications (NBR 7175)</h2>
      <p>Always use certified lime. The most common classifications are:</p>
      <ul>
        <li><strong>Laying Mortar:</strong> Mix ratios like 1:1:6 (Cement:Lime:Sand) offer ideal balance.</li>
        <li><strong>Plaster and Render:</strong> Lime ensures impermeability and a smooth finish.</li>
        <li><strong>Finish Coat:</strong> For refined finishes before painting.</li>
      </ul>
      
      <h2>Cost-Benefit on Site</h2>
      <p>Many try to "save" by removing lime and using only liquid plasticizing additives. The result is often walls with cracks and infiltration after a few years. Lime represents a small fraction of project cost but protects the greatest asset: the structure.</p>
      
      <div id="cal-calculator-placeholder">
        <p>Calculate the amount of lime needed for your mortar.</p>
      </div>
      
      <h2>Quimical Application Tips</h2>
      <p>For best performance, let the mortar "rest" (mature) for 10 to 15 minutes before application. This ensures full activation of hydrated lime properties.</p>
      
      <h2>Conclusion</h2>
      <p>Don't trade the right for the doubtful. Quimical hydrated lime, produced with the purity of Minas Gerais limestone, ensures your plaster and laying work have professional quality and durability for generations.</p>
    `,
        category: 'Civil Construction',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '8',
    slug: 'erros-comuns-uso-cal-industrial',
    image_url: '/media/10errosdousocalquimical.jpg',
    published_at: '2025-06-12',
    translations: {
      pt: {
        title: '10 erros no uso de cal industrial: como evitar perdas e acidentes',
        excerpt:
          'Armazenamento inadequado, dosagem errada e falhas de segurança: veja os 10 principais erros no manuseio de cal industrial e como corrigi-los.',
        content: `
      <h2>Evitando perdas com cal</h2>
      <p>A cal é um produto químico eficiente, mas exige conhecimento técnico. Na nossa experiência apoiando indústrias em todo o Brasil, observamos que problemas de eficiência muitas vezes não vêm do produto, e sim do manuseio.</p>
      
      <p>Listamos os 10 erros mais críticos que vemos em campo e como sua empresa pode evitá-los para garantir segurança e economia.</p>
      
      <h2>1. Armazenamento com umidade (cal virgem)</h2>
      <p><strong>O erro:</strong> Deixar a cal virgem exposta ao ar ou em silos com vedação ruim. <br><strong>A consequência:</strong> A cal hidrata sozinha (“morre”), endurece e perde reatividade. Use sempre silos herméticos e controle FIFO.</p>
      
      <h2>2. Especificação técnica genérica</h2>
      <p><strong>O erro:</strong> Comprar “qualquer cal”. <br><strong>A solução:</strong> Siderurgia precisa de cal reativa; tratamento de água precisa de pureza. Defina o <em>tipo</em> (Calcítica/Dolomítica) e a <em>granulometria</em> correta com a Quimical.</p>
      
      <h2>3. Erros de dosagem (pH instável)</h2>
      <p><strong>O erro:</strong> Dosar “no olho” ou sem manutenção nos dosadores. <br><strong>A solução:</strong> Automatize a dosagem baseada em leitura de pH em tempo real para evitar desperdício (superdosagem) ou ineficiência (subdosagem).</p>
      
      <h2>4. Ignorar variação entre lotes</h2>
      <p>Monitore os laudos de recebimento. Se o teor de CaO muda, a dosagem da planta precisa ser ajustada imediatamente.</p>
      
      <h2>5. Sistemas de transporte inadequados</h2>
      <p>A cal pode ser abrasiva ou entupir tubulações pneumáticas se houver umidade no ar comprimido. Use secadores de ar nas linhas pneumáticas.</p>
      
      <h2>6. Preparo inadequado do leite de cal</h2>
      <p>Uma suspensão muito diluída desperdiça água; muito concentrada entope bicos. Mantenha a concentração ideal de sólidos (10-15%) e agitação constante nos tanques.</p>
      
      <h2>7. Tempo de reação insuficiente</h2>
      <p>A cal precisa de tempo para reagir. Tanques de contato muito pequenos fazem a cal sair no efluente sem realizar a neutralização.</p>
      
      <h2>8. Falhas de segurança (EPI)</h2>
      <p><strong>Crítico:</strong> A cal causa queimaduras químicas. Nunca manuseie sem óculos de visão total, luvas adequadas e proteção respiratória. Instale lava-olhos próximo aos pontos de descarga.</p>
      
      <h2>9. Foco apenas no preço por tonelada</h2>
      <p>Comprar a cal mais barata do mercado costuma sair caro. Alto teor de impurezas (areia) gera custos de limpeza de tanques e descarte de resíduos que superam a economia de compra.</p>
      
      <div id="cal-calculator-placeholder">
        <p>Compare o Custo Total de Propriedade (TCO) entre fornecedores.</p>
      </div>
      
      <h2>10. Falta de indicadores (KPIs)</h2>
      <p>Se você não mede consumo específico (kg de cal / tonelada de produto ou m³ de efluente), não consegue gerenciar. Tenha métricas claras.</p>
      
      <h2>Conclusão</h2>
      <p>A maioria desses erros pode ser corrigida com treinamento e ajustes de processo. A equipe técnica da Quimical está disponível para auditorias e orientação sobre o melhor uso da nossa cal na sua planta.</p>
    `,
        category: 'Operações Industriais',
        author: 'Equipe Quimical',
      },
      en: {
        title: '10 Mistakes in Industrial Lime Use: How to Avoid Losses and Accidents',
        excerpt:
          'Improper storage, wrong dosing, and safety failures: See the 10 main mistakes in handling industrial lime and how to fix them.',
        content: `
      <h2>Avoiding Losses with Lime</h2>
      <p>Lime is an efficient chemical product, but it requires technical knowledge. In our experience supporting industries across Brazil, we have found that efficiency problems often come not from the product but from handling.</p>
      
      <p>We listed the 10 most critical mistakes we observe in the field and how your company can avoid them to ensure safety and savings.</p>
      
      <h2>1. Storage with Moisture (Quicklime)</h2>
      <p><strong>The Mistake:</strong> Leaving quicklime exposed to air or in silos with poor sealing. <br><strong>The Consequence:</strong> The lime hydrates on its own ("dies"), turns to stone, and loses reactivity. Always use hermetic silos and control FIFO.</p>
      
      <h2>2. Generic Technical Specification</h2>
      <p><strong>The Mistake:</strong> Buying "any lime." <br><strong>The Solution:</strong> Steelmaking needs reactive lime; water treatment needs purity. Define the <em>type</em> (Calcitic/Dolomitic) and the correct <em>particle size</em> with Quimical.</p>
      
      <h2>3. Dosing Errors (Unstable pH)</h2>
      <p><strong>The Mistake:</strong> Dosing "by eye" or without maintenance of dosers. <br><strong>The Solution:</strong> Automate dosing based on real-time pH readings to avoid waste (overdosing) or inefficiency (underdosing).</p>
      
      <h2>4. Ignoring Batch Variation</h2>
      <p>Monitor incoming reports. If CaO content changes, plant dosing must be adjusted immediately.</p>
      
      <h2>5. Inadequate Transport Systems</h2>
      <p>Lime can be abrasive or clog pneumatic piping if there is moisture in the compressed air. Use air dryers in pneumatic transport lines.</p>
      
      <h2>6. Poor Lime Milk Preparation</h2>
      <p>A too-dilute suspension wastes too much water; too concentrated clogs nozzles. Maintain the ideal solids concentration (10-15%) and constant agitation in tanks.</p>
      
      <h2>7. Insufficient Reaction Time</h2>
      <p>Lime needs time to react. Contact tanks that are too small cause lime to exit in the effluent without having done its neutralization work.</p>
      
      <h2>8. Safety Failures (PPE)</h2>
      <p><strong>Critical:</strong> Lime causes chemical burns. Never handle without full-vision goggles, proper gloves, and respiratory protection. Install eyewash stations near discharge points.</p>
      
      <h2>9. Focus Only on Price per Ton</h2>
      <p>Buying the cheapest lime on the market usually ends up expensive. High impurity content (grit) generates costs for tank cleaning and waste disposal that exceed the savings in purchase.</p>
      
      <div id="cal-calculator-placeholder">
        <p>Compare the Total Cost of Ownership (TCO) between suppliers.</p>
      </div>
      
      <h2>10. Lack of Indicators (KPIs)</h2>
      <p>If you don't measure specific consumption (kg of lime / ton of product or m³ of effluent), you can't manage it. Have clear metrics.</p>
      
      <h2>Conclusion</h2>
      <p>Most of these errors can be corrected with training and process adjustments. Quimical's technical team is available for audits and guidance on the best use of our lime in your plant.</p>
    `,
        category: 'Industrial Operations',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '9',
    slug: 'cal-industria-papel-celulose',
    image_url: '/media/celulose.jpg',
    published_at: '2025-07-09',
    translations: {
      pt: {
        title: 'Cal na indústria de papel e celulose: processo kraft e caustificação',
        excerpt:
          'Entenda o ciclo da cal na produção de celulose. A importância da pureza do óxido de cálcio na caustificação, forno de cal e recuperação do licor branco.',
        content: `
      <h2>A importância da cal no processo kraft</h2>
      <p>O setor de <strong>papel e celulose</strong> exige insumos de alta confiabilidade. No processo kraft, a cal tem papel central no ciclo de recuperação química, transformando Carbonato de Sódio em Hidróxido de Sódio (licor branco). Sem cal de qualidade, o ciclo fecha com ineficiência.</p>
      
      <p>Para engenheiros de processo, entender as especificações da cal de reposição (make-up) é essencial para manter a estabilidade do forno de cal e da caustificação.</p>
      
      <h2>O processo de caustificação</h2>
      <p>A reação química fundamental é: <em>Na₂CO₃ + Ca(OH)₂ → 2NaOH + CaCO₃</em>. <br>A eficiência dessa conversão depende diretamente da <strong>reatividade da cal</strong> e do teor de CaO disponível. Impurezas inertes não reagem e sobrecarregam o sistema de dregs e grits.</p>
      
      <h3>Especificações críticas para celulose:</h3>
      <ul>
        <li><strong>Alto teor de CaO (>90%):</strong> Maximiza a produção de licor branco.</li>
        <li><strong>Baixa sílica e magnésio:</strong> Evita incrustações e problemas de decantação de lama de cal.</li>
        <li><strong>Granulometria controlada:</strong> Essencial para hidratação uniforme.</li>
      </ul>
      
      <h2>Forno de cal e reposição</h2>
      <p>A lama de cal (CaCO3) é regenerada em fornos rotativos. Porém, ocorrem perdas e é necessário repor com cal virgem externa (make-up). A qualidade dessa cal define a “saúde” de longo prazo do ciclo. Usar cal de baixa pureza acumula contaminantes no circuito fechado da fábrica.</p>
      
      <h2>Tratamento de efluentes e branqueamento</h2>
      <p>Além da produção, a cal é utilizada no tratamento de efluentes da fábrica, corrigindo pH e removendo cor e sólidos suspensos, garantindo que a água devolvida ao rio esteja dentro dos parâmetros ambientais.</p>
      
      <h2>Gestão de custos e logística</h2>
      <p>Fábricas de celulose consomem volumes massivos. A logística de entrega precisa ser impecável. A Quimical oferece:</p>
      <ul>
        <li>Logística robusta para grandes volumes a granel.</li>
        <li>Contratos de fornecimento com estoque de segurança garantido.</li>
        <li>Produto de baixa variabilidade, estabilizando a operação do forno.</li>
      </ul>
      
      <div id="cal-calculator-placeholder">
        <p>Calcule o impacto da pureza da cal na eficiência da caustificação.</p>
      </div>
      
      <h2>Controle de qualidade no recebimento</h2>
      <p>Recomendamos análise rigorosa de cada carga recebida: testes de “Índice de Cal Disponível” e reatividade são obrigatórios para evitar instabilidades na produção de celulose.</p>
      
      <h2>Conclusão</h2>
      <p>A Quimical é parceira estratégica da indústria florestal. Fornecemos cal de alto desempenho que ajuda a maximizar a recuperação química e a eficiência energética das maiores fábricas de papel e celulose do país.</p>
    `,
        category: 'Papel & Celulose',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Lime in the Pulp and Paper Industry: Kraft Process and Causticizing',
        excerpt:
          'Understand the lime cycle in pulp production. The importance of calcium oxide purity in causticizing, lime kiln, and white liquor recovery.',
        content: `
      <h2>The Importance of Lime in the Kraft Process</h2>
      <p>The <strong>Pulp and Paper</strong> sector demands high-reliability inputs. In the Kraft process, lime plays a central role in the chemical recovery cycle, transforming Sodium Carbonate into Sodium Hydroxide (White Liquor). Without quality lime, the cycle closes with inefficiency.</p>
      
      <p>For process engineers, understanding the specifications of make-up lime is essential to maintain the stability of the lime kiln and causticizing.</p>
      
      <h2>The Causticizing Process</h2>
      <p>The fundamental chemical reaction is: <em>Na₂CO₃ + Ca(OH)₂ → 2NaOH + CaCO₃</em>. <br>The efficiency of this conversion depends directly on <strong>lime reactivity</strong> and its available CaO content. Inert impurities do not react and overload the dregs and grits system.</p>
      
      <h3>Critical Specifications for Pulp:</h3>
      <ul>
        <li><strong>High CaO content (>90%):</strong> Maximizes white liquor production.</li>
        <li><strong>Low Silica and Magnesium:</strong> Prevent scaling problems and difficulties in lime mud settling.</li>
        <li><strong>Controlled Particle Size:</strong> Essential for uniform slaking.</li>
      </ul>
      
      <h2>Lime Kiln and Make-up</h2>
      <p>Lime mud (CaCO3) is regenerated in rotary kilns. However, losses occur and need to be replenished with external quicklime (make-up). The quality of this make-up lime defines the long-term "health" of the cycle. Using low-purity lime accumulates contaminants in the factory's closed circuit.</p>
      
      <h2>Effluent Treatment and Bleaching</h2>
      <p>In addition to production, lime is used in the pulp mill's effluent treatment, correcting pH and removing color and suspended solids, ensuring the water returned to the river meets environmental parameters.</p>
      
      <h2>Cost and Logistics Management</h2>
      <p>Pulp mills consume massive volumes. Delivery logistics must be impeccable. Quimical offers:</p>
      <ul>
        <li>Robust logistics for large bulk volumes.</li>
        <li>Supply contracts with guaranteed safety stock.</li>
        <li>Low-variability product, stabilizing kiln operation.</li>
      </ul>
      
      <div id="cal-calculator-placeholder">
        <p>Calculate the impact of lime purity on causticizing efficiency.</p>
      </div>
      
      <h2>Quality Control on Receipt</h2>
      <p>We recommend rigorous analysis of each received load: "Available Lime Index" tests and reactivity are mandatory to avoid instability in pulp production.</p>
      
      <h2>Conclusion</h2>
      <p>Quimical is a strategic partner to the forest-based industry. We supply high-performance lime that helps maximize chemical recovery and energy efficiency for the country's largest pulp and paper mills.</p>
    `,
        category: 'Pulp & Paper',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '10',
    slug: 'logistica-transporte-cal-minas-gerais',
    image_url: '/media/logistica-caminhao-cal.jpg',
    published_at: '2025-08-21',
    translations: {
      pt: {
        title: 'Logística da cal em Minas Gerais: como reduzir frete e otimizar entregas',
        excerpt:
          'O frete pode impactar até 40% do custo da cal. Descubra como rotas otimizadas a partir de Arcos/MG e a escolha entre granel ou big bag podem economizar.',
        content: `
      <h2>O desafio logístico na compra de cal industrial</h2>
      <p>Para indústrias de alto consumo como siderúrgicas, usinas de açúcar e fábricas de celulose, o preço da cal na porta do forno é apenas uma fração do custo total. A logística muitas vezes representa de 30% a 50% do valor final entregue na planta. Em um país de dimensões continentais como o Brasil, estratégia de transporte não é detalhe: é o ponto de equilíbrio da competitividade.</p>
      
      <p>Estar estrategicamente posicionado em relação ao maior polo produtor de cal da América Latina — <strong>Arcos/MG</strong> — é uma grande vantagem da Quimical. Porém, nossa expertise vai além da localização geográfica. Entendemos que cada cliente possui gargalos de recebimento específicos, seja restrição de horário, altura de silo ou capacidade de manobra de carretas.</p>
      
      <h2>Granel, big bag ou saco: a matemática da escolha</h2>
      <p>A decisão sobre embalagem impacta diretamente o Custo Total de Propriedade (TCO). Veja a análise técnica:</p>
      
      <h3>1. Caminhão silo (granel)</h3>
      <p>É a modalidade mais eficiente para consumo acima de 30 toneladas/mês. 
      <br><strong>Vantagens:</strong> Elimina 100% do custo de embalagem (sacos ou ráfia) e custos de destinação de resíduos sólidos. A descarga é pneumática, o que reduz necessidade de mão de obra operacional.
      <br><strong>Requisito:</strong> Sua indústria deve ter silos de armazenamento com sistema adequado de filtro de mangas.</p>
      
      <h3>2. Big bags (1 tonelada)</h3>
      <p>O meio termo ideal para indústrias médias ou operações temporárias (como obras).
      <br><strong>Vantagens:</strong> Flexibilidade de armazenamento (pode ser empilhado em galpões cobertos) e fácil manuseio com empilhadeiras padrão. É a escolha preferida para grandes obras e tratamento de água em ETEs menores.
      <br><strong>Atenção:</strong> Exige logística reversa ou descarte adequado das embalagens vazias.</p>
      
      <h3>3. Sacos (20kg)</h3>
      <p>Recomendado apenas para varejo ou consumo muito baixo. O custo por kg é significativamente maior devido ao ensacamento e ao manuseio manual intensivo.</p>
      
      <h2>A vantagem da distribuição Quimical</h2>
      <p>Muitas indústrias enfrentam dificuldades ao comprar direto das fábricas por causa de filas de carregamento e burocracias, que geram prazos incertos. Como distribuidora consolidada, a Quimical atua como um “buffer” logístico.</p>
      
      <p>Gerenciamos estoques reguladores e temos contratos com transportadoras especializadas em cargas químicas e perigosas. Isso significa que conseguimos atender pedidos fracionados ou cargas emergenciais com agilidade que grandes produtores nem sempre conseguem. Monitoramos rotas para garantir que sua produção não pare por falta de insumo.</p>
      
      <h2>Conclusão</h2>
      <p>Não deixe o frete consumir sua margem de lucro. A equipe logística da Quimical está pronta para analisar sua rota e sugerir o melhor modal de transporte. Fale conosco para uma cotação CIF (frete incluso) e compare a eficiência.</p>
    `,
        category: 'Logística',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Lime Logistics in Minas Gerais: How to Reduce Freight Costs and Optimize Deliveries',
        excerpt:
          'Freight can impact up to 40% of lime costs. Discover how optimized routes from Arcos/MG and the choice between bulk or big bag can save your budget.',
        content: `
      <h2>The Logistics Challenge in Buying Industrial Lime</h2>
      <p>For high-consumption industries such as steel mills, sugar mills, and pulp factories, the lime price at the kiln gate is only a fraction of the total cost. Logistics often represents 30% to 50% of the final product value delivered to the plant. In a country with continental dimensions like Brazil, transport strategy is not a detail: it is the balance point in competitiveness.</p>
      
      <p>Being strategically positioned relative to the largest lime production hub in Latin America — <strong>Arcos/MG</strong> — is Quimical's great advantage. However, our expertise goes beyond geographic location. We understand that each customer has specific receiving bottlenecks, whether time restrictions, silo height, or trailer maneuvering capacity.</p>
      
      <h2>Bulk, Big Bag, or Bags: The Math of the Choice</h2>
      <p>The packaging decision directly impacts Total Cost of Ownership (TCO). See the technical analysis:</p>
      
      <h3>1. Silo Truck (Bulk)</h3>
      <p>It is the most efficient mode for consumption above 30 tons/month. 
      <br><strong>Advantages:</strong> Eliminates 100% of packaging cost (bags or raffia) and costs of solid waste disposal. Unloading is pneumatic, which reduces the need for operational labor at your plant.
      <br><strong>Requirement:</strong> Your industry must have storage silos with proper bag filter systems.</p>
      
      <h3>2. Big Bags (1 ton)</h3>
      <p>The ideal middle ground for mid-size industries or temporary operations (like construction sites).
      <br><strong>Advantages:</strong> Storage flexibility (can be stacked in covered warehouses) and easy handling with standard forklifts. It is the preferred choice for heavy civil construction and water treatment in smaller ETPs.
      <br><strong>Attention:</strong> Requires reverse logistics or proper disposal of empty packaging.</p>
      
      <h3>3. Bags (20kg)</h3>
      <p>Recommended only for retail or very low consumption. The cost per kg is significantly higher due to the bagging process and labor-intensive manual handling.</p>
      
      <h2>The Advantage of Quimical Distribution</h2>
      <p>Many industries struggle when buying directly from factories due to long loading queues and bureaucracy, which create uncertain lead times. As a consolidated distributor, Quimical acts as a logistical "buffer."</p>
      
      <p>We manage buffer stocks and have contracts with carriers specialized in chemical and hazardous loads. This means we can fulfill split orders or emergency loads with agility that large producers often can't match. We monitor routes to ensure your production never stops for lack of input.</p>
      
      <h2>Conclusion</h2>
      <p>Don't let freight consume your profit margin. Quimical's logistics team is ready to analyze your route and suggest the best transport mode. Contact us for a CIF quote (freight included) and compare efficiency.</p>
    `,
        category: 'Logistics',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '11',
    slug: 'cal-para-agricultura-correcao-solo',
    image_url: '/media/cal-agricultura-solo.jpg',
    published_at: '2025-09-05',
    translations: {
      pt: {
        title: 'Cal virgem agrícola vs. calcário: quando usar cada um na correção do solo',
        excerpt:
          'Entenda quando usar cal virgem agrícola para correção rápida da acidez e quando optar pelo calcário. Maximize a produtividade com a escolha certa.',
        content: `
      <h2>O fator tempo no agronegócio</h2>
      <p>No planejamento agrícola, <em>tempo</em> é tudo. Tradicionalmente, produtores utilizam calcário agrícola para corrigir a acidez do solo. O calcário é eficiente e barato, mas tem uma limitação física: baixa solubilidade. Ele precisa de meses de antecedência e chuva para reagir no solo e elevar o pH. Mas o que acontece quando o plantio está próximo e a análise ainda indica acidez alta?</p>
      
      <p>É aí que entra a <strong>cal virgem agrícola</strong>. Diferente do carbonato de cálcio (calcário), a cal é óxido de cálcio (CaO). Sua reação com a água do solo é imediata e vigorosa, liberando hidroxilas que neutralizam o Alumínio tóxico e corrigem o pH em dias, não meses.</p>
      
      <h2>Comparação técnica: poder neutralizante</h2>
      <p>Para o agrônomo ou gestor da fazenda, a métrica que importa é o PRNT (Poder Relativo de Neutralização Total). Enquanto um bom calcário tem PRNT entre 80% e 90%, a cal virgem agrícola facilmente supera 140% a 170%.</p>
      
      <ul>
        <li><strong>Eficiência logística:</strong> Como a cal é mais concentrada, você aplica menos toneladas por hectare para obter o mesmo resultado químico. Isso significa menos frete, menos combustível no distribuidor e menos compactação do solo pelo tráfego de máquinas.</li>
        <li><strong>Liberação de nutrientes:</strong> Solos ácidos “travam” NPK (Nitrogênio, Fósforo e Potássio). Ao corrigir o pH rapidamente com cal, você maximiza o aproveitamento do fertilizante caro que será aplicado no plantio.</li>
      </ul>
      
      <h2>Aplicação: cuidados e técnicas</h2>
      <p>Aplicar cal virgem exige tecnologia. Por ser um pó fino e reativo, não deve ser aplicado em dias de vento para evitar deriva. Recomenda-se o uso de distribuidores com sistema de bica ou proteção. Além disso, o operador deve estar protegido, pois a poeira pode causar irritação.</p>
      
      <h2>O papel da Quimical no agro</h2>
      <p>A Quimical fornece Cal Virgem Agrícola e Cal Calcítica de alta pureza para produtores de grãos, cana-de-açúcar e citros em todo o Brasil. Entendemos a urgência da safra. Se você perdeu a janela de aplicação do calcário, a cal virgem é a ferramenta técnica para salvar sua produtividade.</p>
      
      <h2>Conclusão</h2>
      <p>Não plante em solo ácido. O custo da correção é mínimo comparado à perda de produtividade causada pelo alumínio tóxico. Fale com nossos especialistas para calcular a dosagem correta de cal virgem para sua área.</p>
    `,
        category: 'Agronegócio',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Agricultural Quicklime vs. Limestone: When to Use Each for Soil Correction',
        excerpt:
          'Understand when to use agricultural quicklime for fast acidity correction and when to choose limestone. Maximize your crop yield with the right choice.',
        content: `
      <h2>The Time Factor in Agribusiness</h2>
      <p>In agricultural planning, <em>timing</em> is everything. Traditionally, farmers use agricultural limestone to correct soil acidity. Limestone is efficient and cheap, but it has a physical limitation: low solubility. It needs months of lead time and rainfall to react in the soil and raise pH. But what happens when planting is near and the soil analysis still indicates high acidity?</p>
      
      <p>That's where <strong>Agricultural Quicklime</strong> comes in. Unlike calcium carbonate (limestone), lime is calcium oxide (CaO). Its reaction with soil water is immediate and vigorous, releasing hydroxyls that neutralize toxic Aluminum and correct pH in days, not months.</p>
      
      <h2>Technical Comparison: Neutralizing Power</h2>
      <p>For the agronomist or farm manager, the metric that matters is PRNT (Relative Total Neutralizing Power). While good limestone has PRNT between 80% and 90%, Agricultural Quicklime easily exceeds 140% to 170%.</p>
      
      <ul>
        <li><strong>Logistics Efficiency:</strong> Because lime is more concentrated, you apply fewer tons per hectare to achieve the same chemical result. This means less freight, less fuel on the spreader, and less soil compaction from machine traffic.</li>
        <li><strong>Nutrient Unlocking:</strong> Acid soils "lock" NPK (Nitrogen, Phosphorus, and Potassium). By correcting pH quickly with lime, you maximize the use of the expensive fertilizer that will be applied at planting.</li>
      </ul>
      
      <h2>Application: Care and Techniques</h2>
      <p>Applying quicklime requires technology. As a fine and reactive powder, it should not be applied on windy days to avoid drift. The use of spreaders with spout systems or protection is recommended. In addition, the operator must be protected, as the dust can cause irritation.</p>
      
      <h2>Quimical's Role in Agriculture</h2>
      <p>Quimical supplies Agricultural Quicklime and high-purity Calcitic Lime to grain, sugarcane, and citrus producers across Brazil. We understand crop urgency. If you missed the limestone application window, quicklime is the technical tool to save your productivity.</p>
      
      <h2>Conclusion</h2>
      <p>Don't plant in acidic soil. The cost of correction is minimal compared to the productivity loss caused by toxic aluminum. Talk to our specialists to calculate the correct quicklime dose for your field.</p>
    `,
        category: 'Agribusiness',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '12',
    slug: 'cal-dolomitica-vs-calcitica-diferencas',
    image_url: '/media/rocha-calcitica-dolomitica.jpg',
    published_at: '2025-10-11',
    translations: {
      pt: {
        title: 'Cal dolomítica ou calcítica? Entenda a química e escolha a certa para sua indústria',
        excerpt:
          'Não compre errado. Veja a diferença entre óxido de cálcio e óxido de magnésio e qual tipo de cal sua indústria realmente precisa para evitar perdas.',
        content: `
      <h2>A química por trás do nome</h2>
      <p>No mercado de cal industrial, um erro comum nos departamentos de compras é tratar “cal” como um produto genérico. Porém, a origem geológica da rocha define a aplicação. A diferença fundamental está na proporção entre Cálcio (Ca) e Magnésio (Mg), e comprar o tipo errado pode travar um processo químico ou danificar equipamentos caros.</p>
      
      <h2>Cal calcítica (rica em cálcio)</h2>
      <p>Derivada de rochas com baixo teor de magnésio (geralmente MgO < 5%). Caracteriza-se por alta reatividade química.</p>
      <p><strong>Aplicações exigidas:</strong></p>
      <ul>
        <li><strong>Tratamento de água e efluentes:</strong> O cálcio é mais eficiente na precipitação de fluoretos e fosfatos e na correção de pH. O magnésio da cal dolomítica gera lodo mais difícil de decantar e pode dissolver excessivamente.</li>
        <li><strong>Indústria de papel e celulose:</strong> A caustificação exige cal com o menor teor possível de magnésio, pois o Mg é um inerte que prejudica o ciclo de recuperação e causa incrustações.</li>
        <li><strong>Indústria química e carbonato de cálcio precipitado (PCC):</strong> Requer brancura e pureza do cálcio.</li>
      </ul>
      
      <h2>Cal dolomítica (com magnésio)</h2>
      <p>Derivada de rocha dolomítica, contém Óxido de Magnésio significativo (geralmente MgO > 12% ou até 30% na cal virgem).</p>
      <p><strong>Aplicações exigidas:</strong></p>
      <ul>
        <li><strong>Siderurgia (aciaria):</strong> Principal consumidora. Adicionar MgO à escória é vital para saturá-la e evitar que “coma” o revestimento refratário do forno, que também é magnesiano. Usar apenas cal calcítica na siderurgia reduz drasticamente a vida útil do forno.</li>
        <li><strong>Agricultura:</strong> Quando o solo apresenta deficiência de magnésio (nutriente essencial para fotossíntese), a cal dolomítica corrige a acidez e fertiliza ao mesmo tempo.</li>
        <li><strong>Vidro:</strong> O magnésio melhora resistência mecânica e estabilidade térmica.</li>
      </ul>
      
      <h2>Expertise Quimical</h2>
      <p>A Quimical atua no coração de Minas Gerais, região privilegiada com jazidas de ambos os tipos de rocha. Não somos apenas vendedores; atuamos como consultores técnicos. Quando recebemos um pedido de cotação, nossa primeira pergunta é: “Qual a sua aplicação?”</p>
      
      <p>Isso garante que sua empresa receba o produto com a especificação química exata. Fornecemos laudos detalhados de teor de CaO e MgO por lote, assegurando rastreabilidade e segurança para seu processo industrial.</p>
    `,
        category: 'Produtos',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Dolomitic or Calcitic Lime? Understand the Chemistry and Choose the Right One for Your Industry',
        excerpt:
          "Don't buy the wrong one. Learn the difference between calcium oxide and magnesium oxide and which lime type your industry really needs to avoid losses.",
        content: `
      <h2>The Chemistry Behind the Name</h2>
      <p>In the industrial lime market, a common confusion in procurement departments is treating "lime" as a generic product. However, the geological origin of the rock defines the application. The fundamental difference lies in the proportion between Calcium (Ca) and Magnesium (Mg), and buying the wrong type can stall a chemical process or damage expensive equipment.</p>
      
      <h2>Calcitic Lime (Calcium-Rich)</h2>
      <p>Derived from rocks with low magnesium content (usually MgO < 5%). It is characterized by high chemical reactivity.</p>
      <p><strong>Required Applications:</strong></p>
      <ul>
        <li><strong>Water and Effluent Treatment:</strong> Calcium is more efficient at precipitating fluorides and phosphates and correcting pH. Magnesium from dolomitic lime generates sludge that is harder to settle and can dissolve excessively.</li>
        <li><strong>Pulp and Paper Industry:</strong> The causticizing process requires lime with the lowest possible magnesium, as Mg is an inert that hinders the recovery cycle and causes scaling.</li>
        <li><strong>Chemical Industry and Precipitated Calcium Carbonate (PCC):</strong> Requires calcium whiteness and purity.</li>
      </ul>
      
      <h2>Dolomitic Lime (With Magnesium)</h2>
      <p>Derived from dolomite rock, it contains significant Magnesium Oxide (generally MgO > 12% or up to 30% in quicklime).</p>
      <p><strong>Required Applications:</strong></p>
      <ul>
        <li><strong>Steelmaking (Steel Shop):</strong> This is the main consumer. Adding MgO to slag is vital to saturate it and prevent it from "eating" the furnace refractory lining, which is also magnesia-based. Using only calcitic lime in steelmaking drastically reduces furnace life.</li>
        <li><strong>Agriculture:</strong> When soil shows Magnesium deficiency (an essential nutrient for photosynthesis), dolomitic lime corrects acidity and fertilizes simultaneously.</li>
        <li><strong>Glass:</strong> Magnesium improves glass mechanical strength and thermal stability.</li>
      </ul>
      
      <h2>Quimical's Expertise</h2>
      <p>Quimical operates in the heart of Minas Gerais, a privileged region with deposits of both rocks. We are not just sellers; we act as technical consultants. When we receive a quote request, our first question is: "What is your application?"</p>
      
      <p>This ensures your company receives the product with the exact chemical specification. We provide detailed CaO and MgO content reports for each batch, ensuring traceability and safety for your industrial process.</p>
    `,
        category: 'Products',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '13',
    slug: 'seguranca-manuseio-cal-virgem',
    image_url: '/media/epi-manuseio-cal.jpg',
    published_at: '2025-11-19',
    translations: {
      pt: {
        title: 'NR-26 e manuseio de cal: guia completo de segurança e EPI',
        excerpt:
          'A cal virgem pode causar queimaduras severas. Baixe nosso checklist de EPIs e procedimentos de segurança para atender as normas trabalhistas.',
        content: `
      <h2>O risco invisível da cal virgem</h2>
      <p>A cal virgem (óxido de cálcio) não é um pó inerte. Trata-se de um produto químico reativo que, ao entrar em contato com umidade — água, suor ou lágrimas — inicia uma reação exotérmica violenta. A temperatura pode passar de 100°C em segundos, causando queimaduras químicas e térmicas de terceiro grau, além de cegueira irreversível.</p>
      
      <p>Muitas empresas negligenciam treinamento, tratando a cal como “poeira comum”. A Quimical, comprometida com a segurança de seus parceiros, reforça a necessidade de aderência rigorosa à <strong>NR-26 (Sinalização de Segurança)</strong> e à NR-6 (EPI).</p>
      
      <h2>EPIs essenciais para o recebimento</h2>
      <p>Sua equipe de almoxarifado ou operação nunca deve se aproximar de um caminhão de descarga ou big bag sem o kit completo:</p>
      <ul>
        <li><strong>Óculos de visão total:</strong> Óculos comuns não são suficientes. É necessária vedação total contra partículas em suspensão.</li>
        <li><strong>Proteção respiratória:</strong> Respiradores semifaciais com filtros PFF2 ou PFF3 são obrigatórios para evitar inalação de poeira alcalina, que pode causar lesões graves nas vias aéreas e pulmões.</li>
        <li><strong>Luvas químicas:</strong> Luvas de couro ou algodão são perigosas porque absorvem suor e cal, criando uma “pasta” corrosiva na pele. Utilize somente luvas de PVC, Neoprene ou Nitrílicas.</li>
        <li><strong>Vestuário completo:</strong> Macacão de manga longa (tipo Tyvek) com calça sobre as botas, evitando a entrada de poeira nos calçados.</li>
      </ul>
      
      <h2>Primeiros socorros: o que fazer e o que NÃO fazer</h2>
      <p>O erro mais comum em acidentes com cal virgem é correr para o chuveiro imediatamente. <strong>Se houver grande quantidade de pó na pele, NÃO LAVE IMEDIATAMENTE.</strong> Pequenas quantidades de água ativam a reação térmica e pioram a queimadura.</p>
      
      <p><strong>Protocolo correto:</strong></p>
      <ol>
        <li>Remova o excesso de pó a seco (escovando roupas e pele com cuidado).</li>
        <li>Retire roupas contaminadas.</li>
        <li>Somente após remover o pó visível, lave com bastante água corrente em baixa pressão por pelo menos 15 a 20 minutos.</li>
        <li>Procure atendimento médico imediato, levando a FISPQ do produto.</li>
      </ol>
      
      <h2>Documentação e suporte</h2>
      <p>Uma operação segura é uma operação duradoura. A Quimical fornece a <strong>FISPQ (Ficha de Informação de Segurança de Produtos Químicos)</strong> atualizada para todos os produtos. Se sua equipe precisa de orientação para manuseio seguro, conte conosco para fornecer os dados técnicos necessários.</p>
    `,
        category: 'Segurança do Trabalho',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'NR-26 and Lime Handling: Complete Safety and PPE Guide',
        excerpt:
          'Quicklime can cause severe burns. Download our PPE checklist and safety procedures to align your company with labor standards.',
        content: `
      <h2>The Invisible Risk of Quicklime</h2>
      <p>Quicklime (calcium oxide) is not an inert powder. It is a reactive chemical product that, upon contact with moisture — whether water, sweat, or tears — starts a violent exothermic reaction. The temperature can exceed 100°C in seconds, causing chemical and thermal third-degree burns, as well as irreversible blindness.</p>
      
      <p>Many companies neglect training, treating lime as "common dust." Quimical, committed to the safety of its partners, reinforces the need for strict adherence to <strong>NR-26 (Safety Signage)</strong> and NR-6 (PPE).</p>
      
      <h2>Essential PPE for Receiving</h2>
      <p>Your warehouse or operations team should never approach a unloading truck or big bag without the full kit:</p>
      <ul>
        <li><strong>Full-Vision Goggles:</strong> Regular goggles are not enough. Total sealing against airborne particles is required.</li>
        <li><strong>Respiratory Protection:</strong> Half-mask respirators with PFF2 or PFF3 filters are mandatory to prevent inhalation of alkaline dust, which can cause severe airway and lung injuries.</li>
        <li><strong>Chemical Gloves:</strong> Leather or cotton gloves are dangerous because they absorb sweat and lime, creating a corrosive "poultice" on the skin. Use only PVC, Neoprene, or Nitrile gloves.</li>
        <li><strong>Full Garment:</strong> Long-sleeve coveralls (Tyvek type) with pants over boots, preventing dust from entering footwear.</li>
      </ul>
      
      <h2>First Aid: What to Do and What NOT to Do</h2>
      <p>The most common mistake in quicklime accidents is rushing to the shower immediately. <strong>If there is a large amount of powder on the skin, DO NOT WASH IMMEDIATELY.</strong> Small amounts of water activate the thermal reaction and worsen the burn.</p>
      
      <p><strong>Correct Protocol:</strong></p>
      <ol>
        <li>Remove excess powder dry (brushing clothing and skin gently).</li>
        <li>Remove contaminated clothing.</li>
        <li>Only after removing visible powder, wash with plenty of running water at low pressure for at least 15 to 20 minutes.</li>
        <li>Seek immediate medical attention, bringing the product's SDS.</li>
      </ol>
      
      <h2>Documentation and Support</h2>
      <p>A safe operation is a lasting operation. Quimical provides the updated <strong>SDS (Safety Data Sheet)</strong> for all products. If your team needs guidance on safe handling, count on us to provide the necessary technical data.</p>
    `,
        category: 'Workplace Safety',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '14',
    slug: 'cal-para-tratamento-agua-potavel',
    image_url: '/media/eta-tratamento-agua.jpg',
    published_at: '2025-01-06',
    translations: {
      pt: {
        title: 'Cal hidratada no tratamento de água potável (ETA): pureza e saúde pública',
        excerpt:
          'Como a cal garante água potável em ETAs municipais e industriais. Entenda a importância da pureza e do baixo teor de metais pesados.',
        content: `
      <h2>Responsabilidade no tratamento de água</h2>
      <p>Em Estações de Tratamento de Água (ETAs), sejam municipais ou industriais, a cal hidratada tem papel vital para garantir a saúde pública. Ela é usada no ajuste final de pH (evitando corrosão nas tubulações de distribuição) e na correção de alcalinidade, necessária para uma floculação eficiente.</p>
      
      <p>Porém, existe uma diferença crucial entre “cal para construção” e “cal para tratamento de água”. Usar um produto inadequado em uma ETA pode introduzir contaminantes na água que será consumida pela população, violando resoluções legais e colocando vidas em risco.</p>
      
      <h2>Critérios de pureza: ABNT e Ministério da Saúde</h2>
      <p>A cal destinada ao saneamento deve seguir especificações rigorosas (NBR 10.790). A Quimical distribui cal de alta pureza que atende a três requisitos fundamentais:</p>
      
      <ul>
        <li><strong>Baixo teor de insolúveis:</strong> Cal com muita areia ou argila (insolúveis) gera turbidez na água tratada e aumenta drasticamente a quantidade de lodo nos decantadores, elevando custos de limpeza e descarte.</li>
        <li><strong>Ausência de metais pesados:</strong> O calcário pode conter traços naturais de Chumbo, Cádmio ou Arsênio. Para uso potável, fornecemos produtos com laudos garantindo níveis muito abaixo dos limites máximos permitidos pela Portaria de Consolidação do Ministério da Saúde.</li>
        <li><strong>Reatividade e finura:</strong> Cal micronizada reage mais rápido, garantindo ajuste preciso de pH e evitando partículas não reagidas no produto final.</li>
      </ul>
      
      <h2>Logística dedicada ao saneamento</h2>
      <p>Sabemos que uma ETA não pode parar. A falta de insumos em uma estação é emergência de saúde pública. Por isso, a Quimical possui logística dedicada a companhias de saneamento, com caminhões exclusivos para evitar contaminação cruzada e entrega garantida inclusive em finais de semana e feriados.</p>
      
      <h2>Conclusão</h2>
      <p>Tratamento de água exige seriedade e certificação. Não arrisque a qualidade da água da sua cidade ou da sua indústria. Solicite os laudos técnicos completos da cal Quimical e garanta total conformidade com a legislação ambiental e sanitária.</p>
    `,
        category: 'Saneamento',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Hydrated Lime in Drinking Water Treatment (WTP): Purity and Public Health',
        excerpt:
          'How lime ensures potable water in municipal and industrial WTPs. Understand the importance of purity and low heavy metal content.',
        content: `
      <h2>Responsibility in Water Treatment</h2>
      <p>In Water Treatment Plants (WTPs), whether municipal or industrial, hydrated lime plays a vital role in ensuring public health. It is used for final pH adjustment (preventing corrosion in distribution pipes) and for alkalinity correction, necessary for efficient flocculation.</p>
      
      <p>However, there is a crucial difference between "construction lime" and "lime for water treatment." Using an unsuitable product in a WTP can introduce contaminants into water that will be consumed by the population, violating legal resolutions and putting lives at risk.</p>
      
      <h2>Purity Criteria: ABNT and Ministry of Health</h2>
      <p>Lime intended for sanitation must follow strict specifications (NBR 10.790). Quimical distributes high-purity lime that meets three fundamental requirements:</p>
      
      <ul>
        <li><strong>Low Insoluble Content:</strong> Lime with too much sand or clay (insolubles) creates turbidity in treated water and drastically increases the amount of sludge in clarifiers, raising cleaning and disposal costs.</li>
        <li><strong>Absence of Heavy Metals:</strong> Limestone can contain natural traces of Lead, Cadmium, or Arsenic. For potable use, we supply products with reports ensuring levels of these elements are far below the maximum limits allowed by the Ministry of Health Consolidation Ordinance.</li>
        <li><strong>Reactivity and Fineness:</strong> Micronized lime reacts faster, ensuring precise pH adjustment and preventing unreacted lime particles from reaching the end consumer.</li>
      </ul>
      
      <h2>Dedicated Logistics for Sanitation</h2>
      <p>We know a WTP cannot stop. The lack of chemicals at a treatment plant is a public health emergency. Therefore, Quimical has a logistics system dedicated to sanitation companies, with dedicated trucks to avoid cross-contamination and delivery guaranteed even on weekends or holidays.</p>
      
      <h2>Conclusion</h2>
      <p>Water treatment demands seriousness and certification. Don't risk the quality of your city's water or your industry. Request the complete technical reports for Quimical lime and ensure full compliance with environmental and health legislation.</p>
    `,
        category: 'Sanitation',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '15',
    slug: 'estabilizacao-solo-estradas-cal',
    image_url: '/media/estabilizacao-solo-estrada.jpg',
    published_at: '2025-12-03',
    translations: {
      pt: {
        title: 'Estabilização de solo com cal: solução de engenharia para grandes obras',
        excerpt:
          'Melhore a capacidade de suporte de solos argilosos em projetos de infraestrutura usando cal. Reduza custos com descarte e material importado.',
        content: `
      <h2>O problema dos solos argilosos</h2>
      <p>No Brasil, é comum que grandes obras de infraestrutura — rodovias, ferrovias, aeroportos e loteamentos — encontrem solos argilosos, úmidos e com baixa capacidade de suporte. O método tradicional de “troca de solo” (escavar o material ruim, enviar para bota-fora e importar brita) é caro, lento e ambientalmente agressivo.</p>
      
      <p>A estabilização química com <strong>cal virgem ou cal hidratada</strong> é a solução de engenharia comprovada mundialmente para transformar solos medianos em bases de alto desempenho.</p>
      
      <h2>Como a cal transforma o solo?</h2>
      <p>A cal atua em duas fases distintas:</p>
      <ol>
        <li><strong>Secagem imediata e modificação:</strong> Ao misturar cal virgem em solo úmido, a reação de hidratação consome a água livre e gera calor, secando o solo instantaneamente. Além disso, ocorre troca iônica: o cálcio substitui outros íons na argila, causando floculação. O solo fica friável, fácil de compactar e de trabalhar com máquinas.</li>
        <li><strong>Estabilização pozolânica (longo prazo):</strong> Com o tempo, o cálcio reage com a sílica e a alumina da argila, formando silicatos de cálcio hidratados (semelhantes ao cimento). Isso aumenta drasticamente a resistência (CBR) e torna o solo impermeável e resistente à erosão.</li>
      </ol>
      
      <h2>Benefícios econômicos e operacionais</h2>
      <p>Para construtoras e empreiteiras, usar cal significa:</p>
      <ul>
        <li><strong>Continuidade da obra:</strong> Permite terraplenagem logo após chuva, reduzindo dias de paralisação.</li>
        <li><strong>Redução de espessura de pavimento:</strong> Uma sub-base tratada com cal tem suporte superior, permitindo reduzir a camada de asfalto ou agregado acima, economizando milhões no projeto final.</li>
        <li><strong>Durabilidade:</strong> Estradas estabilizadas com cal não sofrem com ciclos de expansão e retração de argilas, principal causa de trincas e buracos precoces.</li>
      </ul>
      
      <h2>Fornecimento para grandes obras</h2>
      <p>A Quimical está preparada para atender a demanda crescente de grandes canteiros. Fornecemos cal a granel ou em big bags, com capacidade de entregar várias cargas por dia, garantindo o ritmo dos equipamentos de terraplenagem e recicladoras de asfalto.</p>
    `,
        category: 'Obras Pesadas',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Soil Stabilization with Lime: The Engineering Solution for Heavy Works',
        excerpt:
          'Improve bearing capacity of clayey soils in infrastructure projects using lime. Reduce costs with disposal and imported material.',
        content: `
      <h2>The Problem with Clay Soils</h2>
      <p>In Brazil, it is common for major infrastructure projects — highways, railways, airports, and subdivisions — to encounter clayey, moist soils with low bearing capacity. The traditional method of "soil replacement" (excavating the bad material, sending it to disposal, and importing gravel) is expensive, slow, and environmentally aggressive.</p>
      
      <p>Chemical stabilization with <strong>quicklime or hydrated lime</strong> is the globally proven engineering solution to transform mediocre soils into high-performance bases.</p>
      
      <h2>How Does Lime Transform Soil?</h2>
      <p>Lime works in two distinct phases:</p>
      <ol>
        <li><strong>Immediate Drying and Modification:</strong> When mixing quicklime into wet soil, the hydration reaction consumes free water and generates heat, instantly drying the soil. In addition, ion exchange occurs: calcium replaces other ions in the clay, causing flocculation. The soil becomes friable, easy to compact, and workable with machines.</li>
        <li><strong>Pozzolanic Stabilization (Long Term):</strong> Over time, calcium reacts with the silica and alumina in clay, forming hydrated calcium silicates (similar to cement). This dramatically increases strength (CBR) and makes the soil impermeable and resistant to erosion.</li>
      </ol>
      
      <h2>Economic and Operational Benefits</h2>
      <p>For builders and contractors, using lime means:</p>
      <ul>
        <li><strong>Project Continuity:</strong> Allows earthworks right after rain, reducing downtime days.</li>
        <li><strong>Reduced Pavement Thickness:</strong> A lime-treated subbase has superior support, allowing reduction of the asphalt or aggregate layer above, saving millions in the final project.</li>
        <li><strong>Durability:</strong> Roads stabilized with lime don't suffer from clay expansion and contraction cycles, the main cause of early potholes and cracks.</li>
      </ul>
      
      <h2>Supply for Large Projects</h2>
      <p>Quimical is prepared to meet the surging demand of large job sites. We supply lime in bulk or big bags, with the capacity to deliver multiple truckloads per day, ensuring the rhythm of your earthmoving equipment and asphalt recyclers.</p>
    `,
        category: 'Heavy Construction',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '16',
    slug: 'cal-pintura-imobiliaria-tinta',
    image_url: '/media/fabricacao-tintas-cal.jpg',
    published_at: '2025-01-27',
    translations: {
      pt: {
        title: 'Cal micronizada na indústria de tintas: carga funcional de baixo custo',
        excerpt:
          'A cal não é só para construção. Descubra seu uso como carga mineral de baixo custo e alta alvura em tintas, texturas e revestimentos.',
        content: `
      <h2>Além da cal tradicional</h2>
      <p>Quando pensamos em cal e pintura, lembramos da pintura de caiação. Porém, na indústria química moderna, a cal hidratada (hidróxido de cálcio) evoluiu. Hoje, é uma carga mineral técnica essencial em formulações de tintas imobiliárias, texturas, revestimentos e massas corrida.</p>
      
      <p>Para o químico formulador, o desafio é sempre equilibrar qualidade e custo. O Dióxido de Titânio (TiO2) é o pigmento branco por excelência, mas é extremamente caro e dolarizado. A cal micronizada entra como uma carga funcional inteligente para reduzir custo sem perder qualidade.</p>
      
      <h2>Características da cal micronizada Quimical</h2>
      <p>Para uso em tintas, “cal de construção” não serve. A indústria exige um produto processado com rigor:</p>
      <ul>
        <li><strong>Granulometria extrafina (malha 325 ou #400):</strong> A cal precisa ser impalpável. Partículas grossas causam aspereza no acabamento e problemas de dispersão.</li>
        <li><strong>Alta alvura:</strong> Nossa cal possui alvura superior, permitindo agir como extensor de TiO2 mantendo brilho e cobertura de tintas brancas ou pastéis.</li>
        <li><strong>Poder de cobertura a seco:</strong> A cal contribui significativamente para a opacidade do filme seco (poder de cobertura).</li>
        <li><strong>Ação biocida:</strong> A alta alcalinidade da cal ajuda a conservar a tinta na lata e oferece resistência a fungos e mofos na parede, reduzindo necessidade de biocidas caros na formulação.</li>
      </ul>
      
      <h2>Redução de custo na formulação</h2>
      <p>Substituir parcialmente cargas inertes importadas ou carbonatos simples por cal hidratada micronizada de alta qualidade é uma estratégia de engenharia de valor. A Quimical fornece esse insumo em sacos valvulados ou big bags, garantindo pureza e consistência de cor entre lotes — fator crítico para a indústria de tintas.</p>
      
      <h2>Conclusão</h2>
      <p>Se você busca otimizar sua fórmula de tinta texturizada ou econômica, a cal micronizada é a solução. Solicite uma amostra técnica para testes em laboratório e comprove os ganhos de desempenho e custo.</p>
    `,
        category: 'Indústria Química',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Micronized Lime in the Paint Industry: Low-Cost Functional Filler',
        excerpt:
          "Lime isn't only for construction. Discover its use as a low-cost, high-whiteness mineral filler in the manufacture of paints, textures, and textured coatings.",
        content: `
      <h2>Beyond Traditional Whitewash</h2>
      <p>When we think of lime and paint, we remember traditional wall whitewash. However, in the modern chemical industry, hydrated lime (calcium hydroxide) has evolved. Today, it is a technical mineral filler essential in formulations for architectural paints, textures, textured coatings, and skim coats.</p>
      
      <p>For the formulation chemist, the challenge is always to balance quality and cost. Titanium Dioxide (TiO2) is the white pigment par excellence, but it is extremely expensive and priced in dollars. Micronized lime enters as a smart functional filler to reduce formula cost without losing quality.</p>
      
      <h2>Characteristics of Quimical Micronized Lime</h2>
      <p>For use in paints, common "builder's lime" doesn't work. The industry requires a product processed with rigor:</p>
      <ul>
        <li><strong>Extra-fine Particle Size (Mesh 325 or #400):</strong> Lime must be impalpable. Coarse particles cause roughness in the paint finish and dispersion problems.</li>
        <li><strong>High Whiteness:</strong> Our lime has superior whiteness, allowing it to act as a TiO2 extender while maintaining brightness and coverage of white or pastel paint.</li>
        <li><strong>Dry Coverage Power:</strong> Lime significantly contributes to dry film opacity (hiding power).</li>
        <li><strong>Biocidal Action:</strong> Lime's high alkalinity helps preserve paint in the can and provides resistance to fungi and mold on the wall, reducing the need for expensive biocides in the formulation.</li>
      </ul>
      
      <h2>Cost Reduction in Formulation</h2>
      <p>Partially replacing imported inert fillers or simple carbonates with high-quality micronized hydrated lime is a value engineering strategy. Quimical supplies this input in valve bags or big bags, ensuring purity and color consistency between batches, a critical factor for the paint industry.</p>
      
      <h2>Conclusion</h2>
      <p>If you're looking to optimize your texture or economy paint formula, micronized lime is the solution. Request a technical sample for lab tests and prove the performance and cost gains.</p>
    `,
        category: 'Chemical Industry',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '17',
    slug: 'gestao-estoque-cal-industria',
    image_url: '/media/silos-armazenagem-cal.jpg',
    published_at: '2025-03-01',
    translations: {
      pt: {
        title: 'Gestão de estoque de cal: como evitar hidratação e perdas no processo',
        excerpt:
          'A cal virgem “vence” se armazenada de forma inadequada. Dicas práticas para gestores de suprimentos manterem a qualidade do insumo na planta.',
        content: `
      <h2>O inimigo invisível: higroscopicidade</h2>
      <p>Gerenciar estoque de insumos químicos exige conhecimento específico, e com a cal virgem o desafio é físico-químico. Diferente de minerais ou agregados que podem ficar expostos ao tempo, a cal virgem é <strong>higroscópica</strong>: ela absorve umidade do ar com facilidade.</p>
      
      <p>Quando a cal absorve umidade dentro do silo ou saco, inicia uma reação de hidratação parcial. O resultado? O material “empedra”, formando crostas duras que entopem roscas, válvulas rotativas e sistemas de dosagem pneumática. Além da perda mecânica, há perda química: cal que já reagiu no estoque não terá eficiência no seu processo industrial.</p>
      
      <h2>Manual de melhores práticas de armazenagem</h2>
      <p>Para garantir que a cal comprada da Quimical entregue 100% do desempenho, recomendamos aos gestores da planta:</p>
      
      <h3>1. Rotação FIFO rigorosa (First-In, First-Out)</h3>
      <p>O primeiro lote a entrar deve ser o primeiro a sair. A cal virgem tem vida útil técnica curta se a embalagem não for perfeita. Evite estoques parados por mais de 30 dias, especialmente em regiões úmidas.</p>
      
      <h3>2. Inspeção visual dos big bags</h3>
      <p>No recebimento, verifique a integridade das alças e do tecido. Um pequeno rasgo causado pelo garfo da empilhadeira é suficiente para hidratar e petrificar um saco inteiro em poucos dias de chuva. Armazene sempre em galpões cobertos e sobre paletes, nunca diretamente no chão.</p>
      
      <h3>3. Manutenção dos silos</h3>
      <p>Silos de cal devem ser herméticos. Verifique periodicamente as vedações das tampas de inspeção. Essencial: instale filtros desumidificadores (sílica gel ou similar) no respiro do silo. Quando o silo esvazia, ele “puxa” ar externo; se esse ar estiver úmido, a cal vai se agarrar às paredes.</p>
      
      <h2>Solução logística: Just-in-Time</h2>
      <p>A melhor forma de evitar cal velha é comprar a quantidade certa. A Quimical trabalha com entregas programadas para manter seu estoque sempre fresco e reativo. Atuamos como parceiros de planejamento, ajustando entregas ao seu ritmo de produção para evitar excesso de inventário parado.</p>
    `,
        category: 'Gestão Industrial',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Lime Inventory Management: How to Prevent Hydration and Process Losses',
        excerpt:
          'Quicklime "expires" if stored poorly. Practical tips for supply managers to keep input quality in the plant yard.',
        content: `
      <h2>The Hidden Enemy: Hygroscopy</h2>
      <p>Managing chemical input inventory requires specific knowledge, and with quicklime, the challenge is physical-chemical. Unlike minerals or aggregates that can be exposed to the weather, quicklime is <strong>hygroscopic</strong>: it avidly absorbs moisture from the air.</p>
      
      <p>When quicklime absorbs moisture inside the silo or bag, it starts a partial hydration reaction. The result? The material "cakes," forming hard crusts that clog screw conveyors, rotary valves, and pneumatic dosing systems. Beyond mechanical loss, there is chemical loss: lime that has already reacted in storage will not be efficient in your industrial process.</p>
      
      <h2>Best Practices Storage Manual</h2>
      <p>To ensure the lime purchased from Quimical delivers 100% in your application, we recommend to plant managers:</p>
      
      <h3>1. Strict FIFO Rotation (First-In, First-Out)</h3>
      <p>The first batch in MUST be the first out. Quicklime has a short "technical shelf life" if packaging is not perfect. Avoid stocks sitting for more than 30 days, especially in humid regions.</p>
      
      <h3>2. Visual Inspection of Big Bags</h3>
      <p>When receiving the load, check the integrity of loops and fabric. A small hole caused by a forklift tine is enough to hydrate and petrify an entire bag within a few rainy days. Always store in covered sheds and on pallets, never directly on the floor.</p>
      
      <h3>3. Silo Maintenance</h3>
      <p>Lime silos must be airtight. Periodically check gasket seals on manholes. Essential: install dehumidifying filters (silica gel or similar) on the silo vent. When the silo empties, it "sucks" in outside air; if that air is humid, the lime inside will cake onto the walls.</p>
      
      <h2>Logistics Solution: Just-in-Time</h2>
      <p>The best way to avoid old lime is to buy the right amount. Quimical works with scheduled deliveries so your stock is always fresh and reactive. We act as your planning partner, adjusting deliveries to your production rhythm to avoid excess idle inventory.</p>
    `,
        category: 'Industrial Management',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '18',
    slug: 'cal-industria-couro-curtume',
    image_url: '/media/curtume-couro-cal.jpg',
    published_at: '2025-04-22',
    translations: {
      pt: {
        title: 'Uso da cal em curtumes: qualidade no caleiro e depilação',
        excerpt:
          'Entenda o papel essencial da cal no processamento de couro. Especificações de solubilidade e baixo teor de ferro para evitar manchas.',
        content: `
      <h2>A cal na transformação do couro</h2>
      <p>O Brasil é um dos maiores exportadores de couro do mundo, e a qualidade do produto começa nas etapas iniciais de ribeira. O processo de <strong>caleiro</strong> é onde o couro recebe uma carga alcalina pesada (geralmente cal hidratada e sulfeto de sódio) para promover a depilação e o inchaço das fibras de colágeno.</p>
      
      <p>Essa etapa define a maciez e a qualidade da “flor” do couro. Um caleiro mal executado resulta em couro rígido, com rugas ou manchas.</p>
      
      <h2>O grande vilão: contaminação por ferro</h2>
      <p>Para o técnico de curtume, a pureza química da cal é inegociável. O inimigo número um é o óxido de ferro. Se a cal utilizada contém ferro, ele reage com o sulfeto usado na depilação e forma sulfeto de ferro, um composto escuro.</p>
      
      <p>O resultado são manchas profundas, escuras e irreversíveis na flor do couro (“manchas de ferro”). Isso pode condenar um lote inteiro de couro de exportação a virar raspa ou segunda linha, gerando enormes prejuízos.</p>
      
      <h2>Granulometria e solubilidade</h2>
      <p>Além da química, a física importa. A cal para curtumes precisa ser micronizada e ter baixa quantidade de “areia” (grits). Partículas grossas e insolúveis atuam como lixa dentro dos fulões, causando abrasão e riscos na superfície delicada do couro.</p>
      
      <h2>Qualidade Quimical para o setor de couro</h2>
      <p>A Quimical entende a dor do curtume. Selecionamos lotes específicos de cal calcítica com baixo teor de ferro e granulometria extrafina. Nosso produto garante dispersão rápida no fulão, caleiro uniforme e, principalmente, a segurança de que seu couro não terá manchas metálicas. Fornecemos em sacos ou big bags adequados à estrutura do seu curtume.</p>
    `,
        category: 'Curtumes',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'The Use of Lime in Tanneries: Quality in Liming and Unhairing',
        excerpt:
          'Understand the essential role of lime in leather processing. Solubility specifications and low iron content to avoid stains on the final product.',
        content: `
      <h2>Lime in the Transformation of Hide into Leather</h2>
      <p>Brazil is one of the largest leather exporters in the world, and product quality starts in the early beamhouse stages. The <strong>liming</strong> process is where the hide receives a heavy alkaline charge (usually hydrated lime and sodium sulfide) to promote unhairing (removal of hair and epidermis) and swelling of collagen fibers.</p>
      
      <p>This stage defines the softness and quality of the leather "grain." Poor liming results in stiff leather with wrinkles or stains.</p>
      
      <h2>The Main Villain: Iron Contamination</h2>
      <p>For the tannery technician, lime chemical purity is non-negotiable. The number one enemy is iron oxide. If the lime used contains iron, it reacts with the sulfide used in unhairing to form Iron Sulfide, a black compound.</p>
      
      <p>The result is dark, deep, irreversible stains on the leather grain ("iron stains"). This can condemn an entire batch of export-grade leather to become split or second-line product, generating huge losses.</p>
      
      <h2>Particle Size and Solubility</h2>
      <p>Beyond chemistry, physics matters. Lime for tanneries must be micronized and have a low amount of "sand" (grits). Coarse, insoluble particles act like sandpaper inside rotating drums, causing abrasion and scratches on the delicate hide surface.</p>
      
      <h2>Quimical Quality for the Leather Sector</h2>
      <p>Quimical understands the tanner's pain points. We select specific batches of calcitic lime with controlled iron content and extra-fine particle size. Our product ensures rapid dispersion in the drum, uniform liming, and, most importantly, the safety that your leather will not have metal stains. We supply in bags or big bags suitable for your tannery's structure.</p>
    `,
        category: 'Tanneries',
        author: 'Quimical Team',
      },
    },
  },
  {
    id: '19',
    slug: 'sustentabilidade-uso-cal',
    image_url: '/media/sustentabilidade-meio-ambiente.jpg',
    published_at: '2025-12-28',
    translations: {
      pt: {
        title: 'Cal e sustentabilidade: tecnologias ambientais e economia circular',
        excerpt:
          'Como o uso de cal contribui para o meio ambiente, do tratamento de gases poluentes e dessulfuração à recuperação de áreas degradadas.',
        content: `
      <h2>Muito além da indústria: um agente de limpeza ambiental</h2>
      <p>Muitas vezes associada à indústria pesada e mineração, a cal é, paradoxalmente, um dos maiores agentes de proteção ambiental do planeta. Suas propriedades alcalinas fazem dela a principal ferramenta para remediação dos impactos das atividades humanas. Na Quimical, temos orgulho de distribuir um produto essencial para tecnologias limpas e sustentabilidade.</p>
      
      <h2>1. Lavagem de gases e ar limpo (FGD)</h2>
      <p>Termelétricas, incineradores de resíduos e indústrias de vidro queimam combustíveis que liberam gases ácidos, principalmente Óxidos de Enxofre (SOx) e Cloro. Se liberados na atmosfera, causam chuva ácida.
      <br>A cal é usada em sistemas de <strong>dessulfurização de gases (Scrubbers)</strong>. Ela reage com os gases ácidos na chaminé, “sequestrando” enxofre e transformando-o em gesso sintético (sulfato de cálcio), que pode ser usado na construção. É a economia circular em prática: transformar poluição em insumo.</p>
      
      <h2>2. Tratamento de drenagem ácida de mina (DAM)</h2>
      <p>Mineração expõe rochas que, em contato com a chuva, geram águas extremamente ácidas e carregadas de metais. A cal é a barreira de proteção, neutralizando a acidez e precipitando metais antes que a água alcance os rios.</p>
      
      <h2>3. Recuperação de solos degradados</h2>
      <p>Áreas contaminadas por resíduos ou atividades industriais tornam-se estéreis devido à acidez extrema. A aplicação de cal corrige o pH do solo, imobiliza contaminantes e permite o replantio de vegetação nativa, devolvendo vida à área.</p>
      
      <h2>Compromisso logístico sustentável</h2>
      <p>Sustentabilidade também é eficiência. Ao otimizar a logística de distribuição a partir de Arcos/MG, a Quimical busca rotas mais eficientes e transporte com carga plena, reduzindo emissões de CO2 por tonelada de cal entregue. Trabalhamos para que sua empresa possa ser ambientalmente responsável, garantindo o fornecimento contínuo de reagentes que mantêm suas operações limpas.</p>
    `,
        category: 'Meio Ambiente',
        author: 'Equipe Quimical',
      },
      en: {
        title: 'Lime and Sustainability: Environmental Technologies and Circular Economy',
        excerpt:
          'How lime use contributes to the environment, from pollutant gas treatment and desulfurization to the recovery of degraded areas.',
        content: `
      <h2>Far Beyond Industry: An Environmental Cleaning Agent</h2>
      <p>Often associated with heavy industry and mining, lime is, paradoxically, one of the world's greatest environmental protection agents. Its unique alkaline properties make it the primary tool for remediating the impacts of human activity on the planet. At Quimical, we are proud to distribute a product that is essential for clean technologies and sustainability.</p>
      
      <h2>1. Gas Scrubbing and Clean Air (FGD)</h2>
      <p>Thermal power plants, waste incinerators, and glass industries burn fuels that release acidic gases, mainly Sulfur Oxides (SOx) and Chlorine. If released into the atmosphere, they cause acid rain.
      <br>Lime is used in <strong>Flue Gas Desulfurization (Scrubbers)</strong> systems. It reacts with acidic gases in the stack, "sequestering" sulfur and transforming it into synthetic gypsum (calcium sulfate), which can be used in construction. It's the circular economy in practice: turning pollution into input.</p>
      
      <h2>2. Acid Mine Drainage (AMD) Treatment</h2>
      <p>Mining exposes rocks that, in contact with rain, generate extremely acidic waters loaded with metals. Lime is the protective barrier, neutralizing acidity and precipitating metals before water reaches rivers.</p>
      
      <h2>3. Recovery of Degraded Soils</h2>
      <p>Areas contaminated by waste or industrial activities often become sterile due to extreme acidity. Lime application corrects soil pH, immobilizes contaminants, and enables replanting of native vegetation, bringing life back to the area.</p>
      
      <h2>Sustainable Logistics Commitment</h2>
      <p>Sustainability is also efficiency. By optimizing distribution logistics from Arcos/MG, Quimical always seeks the most efficient routes and full-load transport, reducing CO2 emissions per ton of lime delivered. We work so your company can be environmentally responsible, ensuring the continuous supply of reagents that keep your operations clean.</p>
    `,
        category: 'Environment',
        author: 'Quimical Team',
      },
    },
  },
];

const getTranslation = (post: BlogPost, language: Language): BlogPostTranslation => {
  if (language === 'en') {
    return post.translations.en;
  }
  return post.translations.pt;
};

export const getBlogPosts = (language: Language): LocalizedBlogPost[] =>
  blogPosts.map((post) => ({
    id: post.id,
    slug: post.slug,
    image_url: post.image_url,
    published_at: post.published_at,
    ...getTranslation(post, language),
  }));

export const getBlogPostBySlug = (language: Language, slug: string): LocalizedBlogPost | undefined => {
  const post = blogPosts.find((entry) => entry.slug === slug);
  if (!post) {
    return undefined;
  }
  return {
    id: post.id,
    slug: post.slug,
    image_url: post.image_url,
    published_at: post.published_at,
    ...getTranslation(post, language),
  };
};

export const getRecentPosts = (
  language: Language,
  excludeSlug?: string,
  limit: number = 3,
): LocalizedBlogPost[] => {
  return blogPosts
    .filter((post) => post.slug !== excludeSlug)
    .slice(0, limit)
    .map((post) => ({
      id: post.id,
      slug: post.slug,
      image_url: post.image_url,
      published_at: post.published_at,
      ...getTranslation(post, language),
    }));
};

